// ─── SOURCES SCREEN ────────────────────────────────────────────
const SMART_CSV_SOURCE_OPTIONS = [
  ['manual', 'Manual'],
  ['shopify', 'Shopify'],
  ['woocommerce', 'WooCommerce'],
  ['stripe', 'Stripe'],
  ['paypal', 'PayPal'],
  ['kickstarter', 'Kickstarter'],
  ['indiegogo', 'Indiegogo'],
  ['gamefound', 'Gamefound'],
  ['adobe_commerce', 'Adobe Commerce'],
];

const SMART_CSV_FIELDS = [
  { key:'source_id', label:'Order/source ID', aliases:['order id', 'order number', 'order #', 'order no', 'id', 'transaction id', 'payment id', 'charge id', 'checkout session id', 'contribution id', 'pledge id', 'backer number', 'invoice id', 'invoice number', 'reference', 'source id'] },
  { key:'date', label:'Order date', aliases:['date', 'order date', 'created at', 'created date', 'created date utc', 'paid at', 'pledged at', 'pledge date', 'backed on', 'time', 'timestamp'] },
  { key:'first_name', label:'First name', aliases:['first name', 'shipping first name', 'ship to first name', 'recipient first name', 'billing first name', 'customer first name', 'contributor first name'] },
  { key:'last_name', label:'Last name', aliases:['last name', 'shipping last name', 'ship to last name', 'recipient last name', 'billing last name', 'customer last name', 'contributor last name'] },
  { key:'full_name', label:'Full name', aliases:['name', 'full name', 'customer name', 'shipping name', 'ship to name', 'recipient', 'recipient name', 'buyer name', 'payer name', 'backer name', 'contributor name'] },
  { key:'email', label:'Email', aliases:['email', 'email address', 'customer email', 'buyer email', 'payer email', 'from email address', 'billing email', 'ship to email', 'contributor email'] },
  { key:'phone', label:'Phone', aliases:['phone', 'phone number', 'contact phone number', 'shipping phone', 'shipping phone number', 'billing phone', 'billing phone number', 'ship to phone', 'ship to phone number', 'recipient phone', 'recipient phone number'] },
  { key:'address_line1', label:'Address line 1', aliases:['address', 'address 1', 'address line 1', 'address_line_1', 'shipping address', 'shipping address 1', 'shipping street', 'shipping street 1', 'street', 'street 1', 'ship to street', 'ship to street 1', 'shipping address line1'] },
  { key:'city', label:'City', aliases:['city', 'shipping city', 'town city', 'town/city', 'ship to city', 'billing city'] },
  { key:'state', label:'State/region', aliases:['state', 'province', 'region', 'state province', 'shipping state', 'shipping province', 'shipping state province', 'ship to state', 'ship to state province', 'province code'] },
  { key:'postal_code', label:'Postal code', aliases:['postal code', 'postcode', 'post code', 'zip', 'zip code', 'shipping zip', 'shipping postal code', 'ship to zip', 'ship to postal code'] },
  { key:'country', label:'Country', aliases:['country', 'country code', 'shipping country', 'shipping country code', 'ship to country', 'billing country', 'country code'] },
  { key:'product', label:'Product/item', aliases:['product', 'product name', 'item', 'item name', 'item title', 'item description', 'lineitem name', 'line item name', 'description', 'reward', 'reward title', 'reward tier', 'perk', 'perk title', 'pledge tier', 'line_items'] },
  { key:'sku', label:'SKU', aliases:['sku', 'item sku', 'lineitem sku', 'line item sku', 'product sku', 'variant sku', 'item id', 'product id'] },
  { key:'qty', label:'Quantity', aliases:['quantity', 'qty', 'lineitem quantity', 'line item quantity', 'item quantity', 'quantity purchased', 'units'] },
  { key:'amount', label:'Amount', aliases:['amount', 'total', 'order total', 'gross', 'amount funded', 'amount funded usd', 'pledged amount', 'pledge amount', 'total pledge', 'amount charged', 'subtotal', 'lineitem price', 'item price'] },
  { key:'currency', label:'Currency', aliases:['currency', 'currency code', 'presentment currency'] },
  { key:'status', label:'Payment/status', aliases:['status', 'payment status', 'financial status', 'order status', 'contribution status', 'pledge status', 'fulfillment status', 'captured'] },
];

const mergeSmartCsvFields = fields => {
  if (!Array.isArray(fields) || !fields.length) return SMART_CSV_FIELDS;
  const byKey = new Map(SMART_CSV_FIELDS.map(field => [field.key, { ...field, aliases:[...field.aliases] }]));
  for (const field of fields) {
    const key = String(field?.key || '').trim();
    if (!key) continue;
    const current = byKey.get(key) || { key, label:field.label || key, aliases:[] };
    byKey.set(key, {
      ...current,
      label: field.label || current.label,
      aliases: [...new Set([...(current.aliases || []), ...(Array.isArray(field.aliases) ? field.aliases : [])])],
    });
  }
  return [
    ...SMART_CSV_FIELDS.map(field => byKey.get(field.key)).filter(Boolean),
    ...[...byKey.values()].filter(field => !SMART_CSV_FIELDS.some(base => base.key === field.key)),
  ];
};

const smartNormalizeText = value => String(value ?? '')
  .replace(/^\uFEFF/, '')
  .trim()
  .toLowerCase()
  .replace(/&/g, ' and ')
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();

const smartCompactText = value => smartNormalizeText(value).replace(/\s+/g, '');

const smartCsvDelimiter = text => {
  const sample = String(text || '').split(/\r?\n/).slice(0, 8).join('\n');
  const candidates = [',', '\t', ';'];
  const counts = candidates.map(delimiter => {
    let count = 0;
    let inQuotes = false;
    for (let i = 0; i < sample.length; i += 1) {
      const ch = sample[i];
      if (ch === '"') {
        if (inQuotes && sample[i + 1] === '"') i += 1;
        else inQuotes = !inQuotes;
      } else if (ch === delimiter && !inQuotes) {
        count += 1;
      }
    }
    return [delimiter, count];
  });
  counts.sort((a, b) => b[1] - a[1]);
  return counts[0][1] > 0 ? counts[0][0] : ',';
};

const parseSmartDelimited = (text, delimiter = ',') => {
  const rows = [];
  let row = [];
  let cell = '';
  let inQuotes = false;
  const input = String(text || '').replace(/^\uFEFF/, '');
  for (let i = 0; i < input.length; i += 1) {
    const ch = input[i];
    if (ch === '"') {
      if (inQuotes && input[i + 1] === '"') {
        cell += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === delimiter && !inQuotes) {
      row.push(cell);
      cell = '';
    } else if ((ch === '\n' || ch === '\r') && !inQuotes) {
      if (ch === '\r' && input[i + 1] === '\n') i += 1;
      row.push(cell);
      if (row.some(value => String(value).trim())) rows.push(row);
      row = [];
      cell = '';
    } else {
      cell += ch;
    }
  }
  row.push(cell);
  if (row.some(value => String(value).trim())) rows.push(row);
  if (!rows.length) return { headers: [], rows: [] };
  const used = new Map();
  const headers = rows[0].map((header, index) => {
    const base = String(header || `Column ${index + 1}`).trim() || `Column ${index + 1}`;
    const count = used.get(base) || 0;
    used.set(base, count + 1);
    return count ? `${base} ${count + 1}` : base;
  });
  const objects = rows.slice(1).map(values => {
    const obj = {};
    headers.forEach((header, index) => { obj[header] = String(values[index] ?? '').trim(); });
    return obj;
  });
  return { headers, rows: objects };
};

const smartValueScore = (fieldKey, values) => {
  const nonEmpty = values.map(value => String(value || '').trim()).filter(Boolean);
  if (!nonEmpty.length) return 0;
  if (fieldKey === 'email') return nonEmpty.filter(value => /@/.test(value)).length >= Math.ceil(nonEmpty.length / 2) ? 18 : 0;
  if (fieldKey === 'amount') return nonEmpty.filter(value => /[$\u20AC\u00A3]|\d+[.,]?\d*/.test(value)).length >= Math.ceil(nonEmpty.length / 2) ? 10 : 0;
  if (fieldKey === 'qty') return nonEmpty.filter(value => /^\d+$/.test(value)).length >= Math.ceil(nonEmpty.length / 2) ? 14 : 0;
  if (fieldKey === 'date') return nonEmpty.filter(value => /\d{4}[-/]\d{1,2}[-/]\d{1,2}|\d{1,2}[-/]\d{1,2}[-/]\d{2,4}/.test(value)).length >= Math.ceil(nonEmpty.length / 2) ? 12 : 0;
  if (fieldKey === 'currency') return nonEmpty.filter(value => /^[A-Z]{3}$/i.test(value)).length >= Math.ceil(nonEmpty.length / 2) ? 14 : 0;
  if (fieldKey === 'country') return nonEmpty.filter(value => /^[A-Z]{2}$/i.test(value) || /^[A-Za-z][A-Za-z\s.'-]{3,}$/.test(value)).length >= Math.ceil(nonEmpty.length / 2) ? 8 : 0;
  if (fieldKey === 'source_id') return new Set(nonEmpty).size >= Math.ceil(nonEmpty.length * 0.75) ? 8 : 0;
  return 0;
};

const smartHeaderScore = (header, field) => {
  const normalized = smartNormalizeText(header);
  const compact = smartCompactText(header);
  if (!normalized) return 0;
  let best = 0;
  for (const alias of field.aliases) {
    const aliasNorm = smartNormalizeText(alias);
    const aliasCompact = smartCompactText(alias);
    if (!aliasNorm) continue;
    if (compact === aliasCompact) best = Math.max(best, 100);
    else if (compact.includes(aliasCompact) && aliasCompact.length > 4) best = Math.max(best, 76);
    else if (aliasCompact.includes(compact) && compact.length > 5) best = Math.max(best, 62);
    else {
      const tokens = aliasNorm.split(' ').filter(token => token.length > 2);
      if (tokens.length && tokens.every(token => normalized.includes(token))) best = Math.max(best, 58);
    }
  }
  return best;
};

const SMART_CSV_RECIPIENT_FIELDS = new Set([
  'first_name', 'last_name', 'full_name', 'phone',
  'address_line1', 'city', 'state', 'postal_code', 'country',
]);

const smartRecipientHeaderScore = (header, fieldKey) => {
  if (!SMART_CSV_RECIPIENT_FIELDS.has(fieldKey)) return 0;
  const normalized = smartNormalizeText(header);
  if (/\b(shipping|ship to|recipient)\b/.test(normalized)) return 18;
  if (/\b(billing|customer|contributor|payer|buyer|backer)\b/.test(normalized)) return -18;
  return 0;
};

const guessSmartCsvMapping = (headers, rows, fields = SMART_CSV_FIELDS) => {
  const candidates = [];
  for (const field of fields) {
    for (const header of headers) {
      const values = rows.slice(0, 12).map(row => row[header]);
      const rank = smartHeaderScore(header, field) + smartValueScore(field.key, values) + smartRecipientHeaderScore(header, field.key);
      if (rank >= 52) candidates.push({ field:field.key, header, rank, score:Math.min(100, rank) });
    }
  }
  candidates.sort((a, b) => b.rank - a.rank);
  const mapping = {};
  const confidence = {};
  const usedHeaders = new Set();
  const usedFields = new Set();
  for (const candidate of candidates) {
    if (usedHeaders.has(candidate.header) || usedFields.has(candidate.field)) continue;
    mapping[candidate.field] = candidate.header;
    confidence[candidate.field] = candidate.score;
    usedHeaders.add(candidate.header);
    usedFields.add(candidate.field);
  }
  return { mapping, confidence };
};

const guessSmartCsvSource = headers => {
  const normalized = headers.map(smartCompactText).join(' ');
  const has = value => normalized.includes(smartCompactText(value));
  if (has('lineitem quantity') || has('financial status')) return 'shopify';
  if (has('billing first name') || has('shipping postcode') || has('order total')) return 'woocommerce';
  if (has('transaction id') && (has('gross') || has('balance impact'))) return 'paypal';
  if ((has('charge id') || has('checkout session id')) || (has('created date utc') && has('amount refunded'))) return 'stripe';
  if (has('backer number') || has('reward title')) return 'kickstarter';
  if (has('contribution id') || has('perk title')) return 'indiegogo';
  if (has('total pledge') || has('pledge tier')) return 'gamefound';
  return 'manual';
};

const smartMoneyAmount = value => {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const cleaned = raw
    .replace(/[^\d,.-]/g, '')
    .replace(/,(?=\d{3}\b)/g, '')
    .replace(',', '.');
  const parsed = Number.parseFloat(cleaned);
  return Number.isFinite(parsed) ? parsed.toFixed(2) : null;
};

const smartCurrencyFrom = (currencyValue, amountValue) => {
  const explicit = String(currencyValue || '').trim();
  if (/^[A-Za-z]{3}$/.test(explicit)) return explicit.toLowerCase();
  const raw = String(amountValue || '');
  if (raw.includes('\u20AC')) return 'eur';
  if (raw.includes('\u00A3')) return 'gbp';
  if (raw.includes('$')) return 'usd';
  return 'usd';
};

const smartNormalizeDate = value => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const iso = raw.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (iso) return `${iso[1]}-${iso[2].padStart(2, '0')}-${iso[3].padStart(2, '0')}`;
  const us = raw.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})/);
  if (us) {
    const year = us[3].length === 2 ? `20${us[3]}` : us[3];
    return `${year}-${us[1].padStart(2, '0')}-${us[2].padStart(2, '0')}`;
  }
  return raw.split(/[T ]/)[0];
};

const splitSmartName = fullName => {
  const parts = String(fullName || '').trim().split(/\s+/).filter(Boolean);
  return { firstName:parts[0] || '', lastName:parts.slice(1).join(' ') || '' };
};

const smartOrdersFromRows = (rows, mapping, source) => {
  const valueFor = (row, key) => {
    const header = mapping[key];
    return header ? String(row[header] ?? '').trim() : '';
  };
  const grouped = new Map();
  rows.forEach((row, index) => {
    const rawId = valueFor(row, 'source_id') || `row-${index + 1}`;
    const sourceId = `${source || 'manual'}-${rawId}`.replace(/\s+/g, '-');
    const fullName = valueFor(row, 'full_name');
    const split = splitSmartName(fullName);
    const firstName = valueFor(row, 'first_name') || split.firstName;
    const lastName = valueFor(row, 'last_name') || split.lastName;
    const amountRaw = valueFor(row, 'amount');
    const currencyRaw = valueFor(row, 'currency');
    const productName = normalizeProductName(valueFor(row, 'product')) || normalizeProductName(valueFor(row, 'sku'));
    const quantity = Math.max(1, Number.parseInt(valueFor(row, 'qty') || '1', 10) || 1);
    const currency = smartCurrencyFrom(currencyRaw, amountRaw);
    const item = {
      sourceItemId: `${sourceId}-${index + 1}`,
      raw_name: productName,
      sku: valueFor(row, 'sku') || null,
      quantity,
      amount: smartMoneyAmount(amountRaw),
      currency,
    };
    if (!grouped.has(sourceId)) {
      grouped.set(sourceId, {
        source_id: sourceId,
        source,
        date: smartNormalizeDate(valueFor(row, 'date')),
        firstName,
        lastName,
        email: valueFor(row, 'email') || null,
        phone: valueFor(row, 'phone') || null,
        addressLine1: valueFor(row, 'address_line1') || null,
        city: cleanCity(valueFor(row, 'city')) || null,
        state: valueFor(row, 'state') || null,
        postalCode: valueFor(row, 'postal_code') || null,
        country: valueFor(row, 'country') || null,
        product: productName || null,
        qty: quantity,
        amount: smartMoneyAmount(amountRaw),
        currency,
        status: valueFor(row, 'status') || 'paid',
        rawJson: { smartCsv: true, source, rowNumber: index + 1, row, mapping },
        items: [],
      });
    }
    const order = grouped.get(sourceId);
    if (item.raw_name || item.sku) order.items.push(item);
  });
  return [...grouped.values()].map(order => ({
    ...order,
    items: order.items.length ? order.items : null,
  }));
};

const SmartCsvImportModal = ({ onClose, onImported }) => {
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState(null);
  const [mapping, setMapping] = useState({});
  const [confidence, setConfidence] = useState({});
  const [columnFields, setColumnFields] = useState(SMART_CSV_FIELDS);
  const [source, setSource] = useState('manual');
  const [fileName, setFileName] = useState('');
  const [delimiter, setDelimiter] = useState(',');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef();

  useEffect(() => {
    let alive = true;
    api('GET', '/api/ocf/column-aliases').then(({ ok, data }) => {
      if (alive && ok) setColumnFields(mergeSmartCsvFields(data?.fields));
    });
    return () => { alive = false; };
  }, []);

  const handleFile = file => {
    if (!file || !/\.(csv|txt|tsv)$/i.test(file.name)) {
      setError('Upload a CSV, TSV, or text export file.');
      return;
    }
    setError('');
    setRows(null);
    setHeaders([]);
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = event => {
      try {
        const text = event.target.result || '';
        const detectedDelimiter = smartCsvDelimiter(text);
        const parsed = parseSmartDelimited(text, detectedDelimiter);
        if (!parsed.headers.length || !parsed.rows.length) throw new Error('No data rows found.');
        const guessed = guessSmartCsvMapping(parsed.headers, parsed.rows, columnFields);
        const sourceGuess = guessSmartCsvSource(parsed.headers);
        if (sourceGuess === 'shopify' && parsed.headers.includes('Name') && !guessed.mapping.source_id) {
          guessed.mapping.source_id = 'Name';
          guessed.confidence.source_id = 90;
        }
        setDelimiter(detectedDelimiter === '\t' ? 'tab' : detectedDelimiter);
        setHeaders(parsed.headers);
        setRows(parsed.rows);
        setMapping(guessed.mapping);
        setConfidence(guessed.confidence);
        setSource(sourceGuess);
      } catch (err) {
        setError(`Could not read this file: ${err.message}`);
      }
    };
    reader.readAsText(file);
  };

  const setFieldHeader = (fieldKey, header) => {
    setMapping(prev => ({ ...prev, [fieldKey]: header }));
    setConfidence(prev => ({ ...prev, [fieldKey]: header ? (prev[fieldKey] || 65) : 0 }));
  };

  const orders = rows ? smartOrdersFromRows(rows, mapping, source) : [];
  const preview = orders.slice(0, 5);
  const mappedCount = columnFields.filter(field => mapping[field.key]).length;

  const runImport = async () => {
    if (!rows?.length) {
      setError('Upload a CSV first.');
      return;
    }
    if (!mapping.email && !mapping.source_id && !mapping.product) {
      setError('Map at least one ID, email, or product column.');
      return;
    }
    setLoading(true);
    setError('');
    const { ok, data } = await api('POST', '/api/orders/import', { orders });
    setLoading(false);
    if (!ok) {
      setError(data?.error || 'Import failed.');
      return;
    }
    onImported();
  };

  const dropZoneStyle = {
    border:`2px dashed ${dragging ? '#006FDD' : '#D1D5DB'}`,
    borderRadius:10,
    padding:'22px 18px',
    textAlign:'center',
    cursor:'pointer',
    background:dragging ? '#EEF6FF' : '#F9FAFB',
    transition:'all 0.15s',
  };

  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(17,24,39,0.42)', zIndex:1000 }}/>
      <div role="dialog" aria-modal="true" aria-label="Smart CSV Import" style={{ position:'fixed', top:'50%', left:'50%', transform:'translate(-50%, -50%)', zIndex:1001, background:'radial-gradient(circle at 12% 0%, rgba(0,111,221,0.08), transparent 34%), radial-gradient(circle at 88% 0%, rgba(115,208,75,0.10), transparent 30%), rgba(255,255,255,0.98)', border:'1px solid rgba(199,213,230,0.9)', backdropFilter:'blur(12px)', WebkitBackdropFilter:'blur(12px)', borderRadius:12, width:'min(920px, calc(100vw - 28px))', maxHeight:'88vh', overflowY:'auto', boxShadow:'0 22px 54px rgba(15,23,42,0.22)' }}>
        <div style={{ padding:'18px 20px 14px', borderBottom:'1px solid #E5E7EB', display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:14 }}>
          <div style={{ display:'flex', alignItems:'center', gap:11, minWidth:0 }}>
            <div style={{ width:36, height:36, borderRadius:8, background:'#F3F4F6', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <FileSpreadsheet size={20} color="#111827"/>
            </div>
            <div style={{ minWidth:0 }}>
              <div style={{ fontSize:16, fontWeight:900, color:'#111827' }}>Smart CSV Import</div>
              <div style={{ fontSize:12, color:'#6B7280', marginTop:3 }}>Omnifill guesses the source fields, then imports raw values for OCF review.</div>
            </div>
          </div>
          <button onClick={onClose} title="Close" style={{ background:'none', border:'none', color:'#9CA3AF', cursor:'pointer', display:'flex', padding:3 }}>
            <X size={18} strokeWidth={2.2}/>
          </button>
        </div>

        <div style={{ padding:20, display:'grid', gap:16 }}>
          <div
            style={dropZoneStyle}
            onClick={() => fileInputRef.current.click()}
            onDragOver={event => { event.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={event => { event.preventDefault(); setDragging(false); handleFile(event.dataTransfer.files[0]); }}
          >
            <Upload size={23} strokeWidth={1.8} color={rows ? '#006FDD' : '#9CA3AF'} style={{ margin:'0 auto 9px' }}/>
            <div style={{ fontSize:13, fontWeight:800, color:rows ? '#0058B8' : '#374151' }}>
              {rows ? `${fileName || 'CSV'} loaded` : 'Drop an order export here or browse'}
            </div>
            {rows && <div style={{ marginTop:4, fontSize:12, color:'#6B7280' }}>{rows.length} rows, {headers.length} columns, delimiter {delimiter}</div>}
          </div>
          <input ref={fileInputRef} type="file" accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values" style={{ display:'none' }} onChange={event => handleFile(event.target.files[0])}/>

          {error && <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', color:'#991B1B', borderRadius:8, padding:'9px 12px', fontSize:12 }}>{error}</div>}

          {rows && (
            <>
              <div style={{ display:'grid', gridTemplateColumns:'minmax(180px, 240px) minmax(0, 1fr)', gap:12, alignItems:'start' }}>
                <div style={{ display:'grid', gap:6 }}>
                  <label style={{ fontSize:12, color:'#374151', fontWeight:900 }}>Detected source</label>
                  <select value={source} onChange={event => setSource(event.target.value)} style={{ border:'1px solid #D1D5DB', borderRadius:8, padding:'9px 10px', fontSize:13, background:'#fff', color:'#111827', outline:'none' }}>
                    {SMART_CSV_SOURCE_OPTIONS.map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <div style={{ border:'1px solid #E5E7EB', borderRadius:8, background:'#F9FAFB', padding:'10px 12px', fontSize:12, color:'#4B5563', lineHeight:1.45 }}>
                  {mappedCount} fields mapped. Adjust any incorrect guesses before importing.
                </div>
              </div>

              <div style={{ border:'1px solid #E5E7EB', borderRadius:8, overflow:'hidden' }}>
                <div style={{ padding:'10px 12px', borderBottom:'1px solid #E5E7EB', background:'#fff', fontSize:12, fontWeight:900, color:'#111827' }}>Column mapping</div>
                <div style={{ maxHeight:250, overflowY:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                    <thead>
                      <tr style={{ background:'#F9FAFB', borderBottom:'1px solid #E5E7EB' }}>
                        <th style={{ padding:'8px 10px', textAlign:'left', color:'#6B7280', fontWeight:900 }}>Field</th>
                        <th style={{ padding:'8px 10px', textAlign:'left', color:'#6B7280', fontWeight:900 }}>CSV column</th>
                        <th style={{ padding:'8px 10px', textAlign:'left', color:'#6B7280', fontWeight:900, width:100 }}>Confidence</th>
                      </tr>
                    </thead>
                    <tbody>
                      {columnFields.map(field => (
                        <tr key={field.key} style={{ borderBottom:'1px solid #F3F4F6' }}>
                          <td style={{ padding:'7px 10px', color:'#111827', fontWeight:800 }}>{field.label}</td>
                          <td style={{ padding:'7px 10px' }}>
                            <select value={mapping[field.key] || ''} onChange={event => setFieldHeader(field.key, event.target.value)} style={{ width:'100%', border:'1px solid #D1D5DB', borderRadius:7, padding:'7px 8px', fontSize:12, background:'#fff', color:'#111827', outline:'none' }}>
                              <option value="">Not mapped</option>
                              {headers.map(header => <option key={header} value={header}>{header}</option>)}
                            </select>
                          </td>
                          <td style={{ padding:'7px 10px', color:'#6B7280', fontWeight:800 }}>{mapping[field.key] ? `${Math.round(confidence[field.key] || 65)}%` : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div style={{ border:'1px solid #E5E7EB', borderRadius:8, overflow:'hidden' }}>
                <div style={{ padding:'10px 12px', borderBottom:'1px solid #E5E7EB', background:'#fff', fontSize:12, fontWeight:900, color:'#111827' }}>Preview</div>
                <div style={{ overflowX:'auto' }}>
                  <table style={{ width:'100%', minWidth:760, borderCollapse:'collapse', fontSize:12 }}>
                    <thead>
                      <tr style={{ background:'#F9FAFB', borderBottom:'1px solid #E5E7EB' }}>
                        {['Source ID', 'Name', 'Email', 'City', 'Country', 'Product', 'Qty', 'Amount'].map(header => (
                          <th key={header} style={{ padding:'8px 10px', textAlign:'left', color:'#6B7280', fontWeight:900 }}>{header}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.map((order, index) => (
                        <tr key={`${order.source_id}-${index}`} style={{ borderBottom:'1px solid #F3F4F6' }}>
                          <td style={{ padding:'8px 10px', fontFamily:'monospace', color:'#374151', maxWidth:150, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{order.source_id}</td>
                          <td style={{ padding:'8px 10px', color:'#111827' }}>{[order.firstName, order.lastName].filter(Boolean).join(' ') || '-'}</td>
                          <td style={{ padding:'8px 10px', color:'#6B7280', maxWidth:150, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{order.email || '-'}</td>
                          <td style={{ padding:'8px 10px', color:'#6B7280' }}>{order.city || '-'}</td>
                          <td style={{ padding:'8px 10px', color:'#6B7280' }}>{order.country || '-'}</td>
                          <td style={{ padding:'8px 10px', color:'#111827', maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{order.product || order.items?.[0]?.sku || '-'}</td>
                          <td style={{ padding:'8px 10px', color:'#374151', fontWeight:800 }}>{order.qty || 1}</td>
                          <td style={{ padding:'8px 10px', color:'#374151', fontFamily:'monospace' }}>{order.amount || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          <div style={{ display:'flex', justifyContent:'flex-end', gap:8 }}>
            <button type="button" onClick={onClose} disabled={loading} style={{ background:'#fff', color:'#374151', border:'1px solid #D1D5DB', borderRadius:8, padding:'9px 13px', fontSize:13, fontWeight:800, cursor:loading ? 'wait' : 'pointer' }}>
              Cancel
            </button>
            <button type="button" onClick={runImport} disabled={loading || !rows} style={{ background:(!rows || loading) ? '#9CA3AF' : '#006FDD', color:'#fff', border:'none', borderRadius:8, padding:'9px 14px', fontSize:13, fontWeight:900, cursor:(!rows || loading) ? 'not-allowed' : 'pointer' }}>
              {loading ? 'Importing...' : rows ? `Import ${orders.length} orders` : 'Upload CSV'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

const KickstarterCsvModal = ({ onClose, onImported }) => {
  const [rows, setRows]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [result, setResult]   = useState(null);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef();

  const parseCsvLine = (line) => {
    const out = []; let cur = ''; let inQ = false;
    for (let i = 0; i < line.length; i++) {
      if (line[i] === '"') {
        if (inQ && line[i+1] === '"') { cur += '"'; i++; }
        else inQ = !inQ;
      } else if (line[i] === ',' && !inQ) { out.push(cur); cur = ''; }
      else cur += line[i];
    }
    out.push(cur);
    return out;
  };

  const parseCsv = (text) => {
    const clean = text.replace(/^\uFEFF/, ''); // strip UTF-8 BOM
    const lines = clean.trim().split(/\r?\n/);
    const headers = parseCsvLine(lines[0]).map(h => h.trim());
    return lines.slice(1).filter(l => l.trim()).map(line => {
      const vals = parseCsvLine(line);
      const obj = {};
      headers.forEach((h, i) => { obj[h] = (vals[i] || '').trim(); });
      return obj;
    });
  };

  // Case-insensitive exact column lookup with multiple fallback names
  const col = (row, ...names) => {
    const keys = Object.keys(row);
    for (const name of names) {
      const lo = name.toLowerCase();
      const found = keys.find(k => k.toLowerCase() === lo);
      if (found !== undefined && row[found] !== '') return row[found];
    }
    return '';
  };

  // Substring column lookup - finds the first key whose name contains any of the given substrings
  const colContains = (row, ...substrings) => {
    const keys = Object.keys(row);
    for (const sub of substrings) {
      const lo = sub.toLowerCase();
      const found = keys.find(k => k.toLowerCase().includes(lo));
      if (found !== undefined && row[found] !== '') return row[found];
    }
    return '';
  };

  // Normalize any date string to YYYY-MM-DD (ISO 8601, dashes)
  const normalizeDate = (raw) => {
    if (!raw) return '';
    // Already ISO with dashes: "2024-01-15" or "2024-01-15T10:30:00Z" or "2024-01-15 10:30:00 UTC"
    const isoMatch = raw.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoMatch) return isoMatch[1];
    // YYYY/MM/DD (slash-separated year-first)
    const ymdSlash = raw.match(/^(\d{4})\/(\d{2})\/(\d{2})/);
    if (ymdSlash) return `${ymdSlash[1]}-${ymdSlash[2]}-${ymdSlash[3]}`;
    // US format MM/DD/YYYY
    const usMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (usMatch) return `${usMatch[3]}-${usMatch[1].padStart(2,'0')}-${usMatch[2].padStart(2,'0')}`;
    return raw.split(/[T ]/)[0];
  };

  const mapRow = (row, idx) => {
    const name      = col(row, 'Shipping Name', 'Backer Name', 'Name', 'Full Name');
    const parts     = name.trim().split(/\s+/);
    const firstName = parts[0] || '';
    const lastName  = parts.slice(1).join(' ') || '';
    // Exact matches first; fuzzy "contains pledge amount" catches currency variants like "Pledge Amount (EUR)"
    const amountRaw = col(row, 'Pledged Amount (USD)', 'Pledged Amount', 'Amount (USD)', 'Pledge Amount (USD)', 'Pledge Amount', 'Amount', 'Total')
      || colContains(row, 'pledge amount', 'pledged amount', 'amount');
    // Exact matches first; fuzzy catches renamed date columns
    const dateRaw   = col(row, 'Pledged At', 'Pledge Date', 'Backed On', 'Created At', 'Date')
      || colContains(row, 'pledged at', 'backed at', 'backed on', 'pledge date');
    const rawId     = col(row, 'Backer UID', 'Pledge ID', 'Backer Number', 'ID');
    const amountStr = amountRaw.replace(/[^0-9.]/g, '');
    const parsed    = parseFloat(amountStr);
    // Detect currency from the amount column header (e.g. "Pledge Amount (EUR)")
    const amountKey = Object.keys(row).find(k => /pledge.?amount|pledged.?amount/i.test(k)) || '';
    const currMatch = amountKey.match(/\(([A-Z]{3})\)/i);
    const currency  = currMatch ? currMatch[1].toLowerCase() : 'usd';
    return {
      source_id:    rawId ? `ks-${rawId}` : `ks-row-${idx + 1}`,
      source:       'kickstarter',
      firstName,
      lastName,
      email:        col(row, 'Email', 'Email Address'),
      addressLine1: col(row, 'Shipping Address 1', 'Shipping Address', 'Address 1', 'Address'),
      city:         cleanCity(col(row, 'Shipping City', 'City')),
      state:        col(row, 'Shipping State', 'Shipping State/Province', 'State', 'Province', 'Region'),
      postalCode:   col(row, 'Shipping Postal Code', 'Shipping Zip', 'Postal Code', 'Zip'),
      country:      col(row, 'Shipping Country Code', 'Shipping Country', 'Country Code', 'Country'),
      product:      col(row, 'Reward Title', 'Reward', 'Product', 'Item'),
      qty:          1,
      amount:       isNaN(parsed) ? null : parsed.toFixed(2),
      currency,
      status:       col(row, 'Pledge Status', 'Payment Status', 'Status') || colContains(row, 'status') || 'paid',
      phone:        col(row, 'Shipping Phone Number', 'Shipping Phone', 'Phone Number', 'Phone') || colContains(row, 'phone') || null,
      date:         normalizeDate(dateRaw),
    };
  };

  const handleFile = (f) => {
    if (!f || !f.name.endsWith('.csv')) { setError('Please upload a .csv file'); return; }
    setError(''); setResult(null); setRows(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try { setRows(parseCsv(e.target.result)); }
      catch (err) { setError('Failed to parse CSV: ' + err.message); }
    };
    reader.readAsText(f);
  };

  const runImport = async () => {
    if (!rows || rows.length === 0) { setError('No rows found in CSV'); return; }
    const orders = rows.map((r, i) => mapRow(r, i)).filter(o => o.source_id || o.email);
    if (orders.length === 0) { setError('Could not map any valid orders - check your CSV columns'); return; }
    setLoading(true); setError('');
    const { ok, data } = await api('POST', '/api/orders/import', { orders });
    setLoading(false);
    if (!ok) { setError(data.error || 'Import failed'); return; }
    setResult(data);
    onImported();
  };

  const preview      = rows ? rows.slice(0, 3).map((r, i) => mapRow(r, i)) : [];
  const detectedCols = rows && rows[0] ? Object.keys(rows[0]) : [];

  const dropZoneStyle = {
    border: `2px dashed ${dragging ? '#05CE78' : '#D1D5DB'}`,
    borderRadius:10, padding:'28px 20px', textAlign:'center', cursor:'pointer',
    background: dragging ? '#E6FAF2' : '#F9FAFB', transition:'all 0.15s', marginBottom:16,
  };

  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.25)', zIndex:40 }}/>
      <div style={{ position:'fixed', top:'50%', left:'50%', transform:'translate(-50%,-50%)', zIndex:50, background:'radial-gradient(circle at 12% 0%, rgba(0,111,221,0.08), transparent 34%), radial-gradient(circle at 88% 0%, rgba(115,208,75,0.10), transparent 30%), rgba(255,255,255,0.96)', border:'1px solid rgba(199,213,230,0.9)', backdropFilter:'blur(12px)', WebkitBackdropFilter:'blur(12px)', borderRadius:16, padding:'28px 32px', width:520, maxHeight:'85vh', overflowY:'auto', boxShadow:'0 8px 40px rgba(0,0,0,0.16)' }}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:22 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ width:32, height:32, borderRadius:8, background:'#E6FAF2', display:'flex', alignItems:'center', justifyContent:'center', padding:6 }}>
              <img src="/assets/payment_processors/logo/kickstarter.webp" alt="" style={{ width:'100%', objectFit:'contain' }}/>
            </div>
            <span style={{ fontWeight:700, fontSize:15 }}>Import Kickstarter CSV</span>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, color:'#9CA3AF', cursor:'pointer', lineHeight:1 }}>×</button>
        </div>

        <div
          style={dropZoneStyle}
          onClick={() => fileInputRef.current.click()}
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
        >
          <Upload size={24} strokeWidth={1.5} color={rows ? '#05CE78' : '#9CA3AF'} style={{ margin:'0 auto 10px' }}/>
          {rows
            ? <div style={{ fontSize:13, fontWeight:600, color:'#05CE78' }}>✓ {rows.length} row{rows.length !== 1 ? 's' : ''} loaded</div>
            : <div style={{ fontSize:13, color:'#6B7280' }}>Drop your Kickstarter CSV here or <span style={{ color:'#05CE78', fontWeight:600 }}>browse</span></div>
          }
          {rows && <div style={{ fontSize:12, color:'#9CA3AF', marginTop:4 }}>Click to replace</div>}
        </div>
        <input ref={fileInputRef} type="file" accept=".csv" style={{ display:'none' }} onChange={e => handleFile(e.target.files[0])}/>

        {detectedCols.length > 0 && (
          <div style={{ marginBottom:12, padding:'8px 12px', background:'#F9FAFB', border:'1px solid #F3F4F6', borderRadius:8, fontSize:11, color:'#6B7280' }}>
            <span style={{ fontWeight:600, color:'#374151' }}>Detected columns: </span>
            {detectedCols.join(', ')}
          </div>
        )}

        {preview.length > 0 && (
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:11, fontWeight:700, color:'#9CA3AF', letterSpacing:'0.08em', marginBottom:8 }}>PREVIEW (first {preview.length} rows)</div>
            <div style={{ border:'1px solid #E5E7EB', borderRadius:8, overflow:'hidden' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                <thead>
                  <tr style={{ background:'#F9FAFB' }}>
                    {['Order ID','Name','Email','City','Country','Amount','Date'].map(h => (
                      <th key={h} style={{ padding:'6px 10px', textAlign:'left', fontSize:10, fontWeight:600, color:'#9CA3AF', borderBottom:'1px solid #E5E7EB', whiteSpace:'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.map((r, i) => (
                    <tr key={i} style={{ borderBottom: i < preview.length-1 ? '1px solid #F3F4F6' : 'none' }}>
                      <td style={{ padding:'6px 10px', fontFamily:'monospace', color:'#374151', fontSize:11 }}>{r.id || '-'}</td>
                      <td style={{ padding:'6px 10px' }}>{[r.firstName, r.lastName].filter(Boolean).join(' ') || '-'}</td>
                      <td style={{ padding:'6px 10px', color:'#6B7280', maxWidth:110, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.email || '-'}</td>
                      <td style={{ padding:'6px 10px', color:'#9CA3AF' }}>{r.city || '-'}</td>
                      <td style={{ padding:'6px 10px', color:'#9CA3AF' }}>{r.country || '-'}</td>
                      <td style={{ padding:'6px 10px', fontFamily:'monospace', fontWeight:600, color: r.amount == null ? '#EF4444' : '#1A1A2E' }}>{r.amount != null ? `$${r.amount}` : '?'}</td>
                      <td style={{ padding:'6px 10px', color: r.date ? '#9CA3AF' : '#EF4444', fontSize:11 }}>{r.date || '?'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {error && <div style={{ marginBottom:12, padding:'8px 12px', background:'#FEE2E2', borderRadius:8, fontSize:12, color:'#991B1B' }}>{error}</div>}

        {result && (
          <div style={{ marginBottom:12, padding:'8px 12px', background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:8, fontSize:12, color:'#065F46' }}>
            ✓ Imported {result.inserted} new orders ({result.total} rows processed)
          </div>
        )}

        <button onClick={runImport} disabled={loading || !rows} style={{
          width:'100%', background: (!rows || loading) ? '#A7F3D0' : '#05CE78',
          color:'#fff', border:'none', borderRadius:8, padding:'10px',
          fontSize:13, fontWeight:600, cursor: (!rows || loading) ? 'not-allowed' : 'pointer',
        }}>
          {loading ? 'Importing…' : rows ? `Import ${rows.length} orders` : 'Upload a CSV first'}
        </button>
      </div>
    </>
  );
};

const IndiegogoCsvModal = ({ onClose, onImported }) => {
  const [rows, setRows]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [result, setResult]     = useState(null);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef();

  const parseCsvLine = (line) => {
    const out = []; let cur = ''; let inQ = false;
    for (let i = 0; i < line.length; i++) {
      if (line[i] === '"') {
        if (inQ && line[i+1] === '"') { cur += '"'; i++; }
        else inQ = !inQ;
      } else if (line[i] === ',' && !inQ) { out.push(cur); cur = ''; }
      else cur += line[i];
    }
    out.push(cur);
    return out;
  };

  const parseCsv = (text) => {
    const clean = text.replace(/^﻿/, '');
    const lines = clean.trim().split(/\r?\n/);
    const headers = parseCsvLine(lines[0]).map(h => h.trim());
    return lines.slice(1).filter(l => l.trim()).map(line => {
      const vals = parseCsvLine(line);
      const obj = {};
      headers.forEach((h, i) => { obj[h] = (vals[i] || '').trim(); });
      return obj;
    });
  };

  const col = (row, ...names) => {
    const keys = Object.keys(row);
    for (const name of names) {
      const lo = name.toLowerCase();
      const found = keys.find(k => k.toLowerCase() === lo);
      if (found !== undefined && row[found] !== '') return row[found];
    }
    return '';
  };

  const colContains = (row, ...substrings) => {
    const keys = Object.keys(row);
    for (const sub of substrings) {
      const lo = sub.toLowerCase();
      const found = keys.find(k => k.toLowerCase().includes(lo));
      if (found !== undefined && row[found] !== '') return row[found];
    }
    return '';
  };

  const normalizeDate = (raw) => {
    if (!raw) return '';
    const isoMatch = raw.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoMatch) return isoMatch[1];
    const ymdSlash = raw.match(/^(\d{4})\/(\d{2})\/(\d{2})/);
    if (ymdSlash) return `${ymdSlash[1]}-${ymdSlash[2]}-${ymdSlash[3]}`;
    const usMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (usMatch) return `${usMatch[3]}-${usMatch[1].padStart(2,'0')}-${usMatch[2].padStart(2,'0')}`;
    return raw.split(/[T ]/)[0];
  };

  const mapRow = (row, idx) => {
    const firstName = col(row, 'Ship To First Name', 'Contributor First Name', 'First Name');
    const lastName  = col(row, 'Ship To Last Name',  'Contributor Last Name',  'Last Name');
    const combined  = (!firstName && !lastName)
      ? (col(row, 'Ship To Name', 'Contributor Name', 'Backer Name', 'Name') || '').trim() : '';
    const parts     = combined.split(/\s+/);
    const first     = firstName || parts[0] || '';
    const last      = lastName  || parts.slice(1).join(' ') || '';
    const amountRaw = col(row, 'Amount Funded (USD)', 'Amount Funded', 'Perk Amount', 'Amount', 'Total')
      || colContains(row, 'amount funded', 'perk amount', 'amount');
    const dateRaw   = col(row, 'Order Date', 'Created At', 'Date')
      || colContains(row, 'order date', 'created at');
    const rawId     = col(row, 'Contribution ID', 'Order ID', 'ID');
    const amountStr = amountRaw.replace(/[^0-9.]/g, '');
    const parsed    = parseFloat(amountStr);
    const amountKey = Object.keys(row).find(k => /amount.?funded|perk.?amount/i.test(k)) || '';
    const currMatch = amountKey.match(/\(([A-Z]{3})\)/i);
    const currency  = currMatch ? currMatch[1].toLowerCase() : 'usd';
    const qty       = parseInt(col(row, 'Quantity', 'Qty') || '1', 10) || 1;
    return {
      source_id:    rawId ? `ig-${rawId}` : `ig-row-${idx + 1}`,
      source:       'indiegogo',
      firstName:    first,
      lastName:     last,
      email:        col(row, 'Email', 'Contributor Email', 'Ship To Email'),
      phone:        col(row, 'Ship To Phone', 'Phone'),
      addressLine1: col(row, 'Ship To Street 1', 'Ship To Street', 'Shipping Address', 'Address 1', 'Address'),
      city:         col(row, 'Ship To City', 'City'),
      state:        col(row, 'Ship To State', 'Ship To State/Province', 'State', 'Province', 'Region'),
      postalCode:   col(row, 'Ship To Zip', 'Ship To Postal Code', 'Postal Code', 'Zip'),
      country:      col(row, 'Ship To Country', 'Country Code', 'Country'),
      product:      col(row, 'Perk Title', 'Perk', 'Reward', 'Tier'),
      qty,
      amount:       isNaN(parsed) ? null : parsed.toFixed(2),
      currency,
      status:       col(row, 'Contribution Status', 'Order Status', 'Payment Status', 'Status') || colContains(row, 'status') || 'paid',
      date:         normalizeDate(dateRaw),
    };
  };

  const handleFile = (f) => {
    if (!f || !f.name.endsWith('.csv')) { setError('Please upload a .csv file'); return; }
    setError(''); setResult(null); setRows(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try { setRows(parseCsv(e.target.result)); }
      catch (err) { setError('Failed to parse CSV: ' + err.message); }
    };
    reader.readAsText(f);
  };

  const runImport = async () => {
    if (!rows || rows.length === 0) { setError('No rows found in CSV'); return; }
    const orders = rows.map((r, i) => mapRow(r, i)).filter(o => o.source_id || o.email);
    if (orders.length === 0) { setError('Could not map any valid orders - check your CSV columns'); return; }
    setLoading(true); setError('');
    const { ok, data } = await api('POST', '/api/orders/import', { orders });
    setLoading(false);
    if (!ok) { setError(data.error || 'Import failed'); return; }
    setResult(data);
    onImported();
  };

  const preview      = rows ? rows.slice(0, 3).map((r, i) => mapRow(r, i)) : [];
  const detectedCols = rows && rows[0] ? Object.keys(rows[0]) : [];

  const dropZoneStyle = {
    border: `2px dashed ${dragging ? '#EB1478' : '#D1D5DB'}`,
    borderRadius:10, padding:'28px 20px', textAlign:'center', cursor:'pointer',
    background: dragging ? '#FCE3F1' : '#F9FAFB', transition:'all 0.15s', marginBottom:16,
  };

  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.25)', zIndex:40 }}/>
      <div style={{ position:'fixed', top:'50%', left:'50%', transform:'translate(-50%,-50%)', zIndex:50, background:'radial-gradient(circle at 12% 0%, rgba(0,111,221,0.08), transparent 34%), radial-gradient(circle at 88% 0%, rgba(115,208,75,0.10), transparent 30%), rgba(255,255,255,0.96)', border:'1px solid rgba(199,213,230,0.9)', backdropFilter:'blur(12px)', WebkitBackdropFilter:'blur(12px)', borderRadius:16, padding:'28px 32px', width:520, maxHeight:'85vh', overflowY:'auto', boxShadow:'0 8px 40px rgba(0,0,0,0.16)' }}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:22 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ width:32, height:32, borderRadius:8, background:'#FCE3F1', display:'flex', alignItems:'center', justifyContent:'center', padding:6 }}>
              <img src="/assets/payment_processors/logo/indiegogo.webp" alt="" style={{ width:'100%', height:'100%', objectFit:'contain' }}/>
            </div>
            <span style={{ fontWeight:700, fontSize:15 }}>Import Indiegogo CSV</span>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, color:'#9CA3AF', cursor:'pointer', lineHeight:1 }}>×</button>
        </div>

        <div
          style={dropZoneStyle}
          onClick={() => fileInputRef.current.click()}
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
        >
          <Upload size={24} strokeWidth={1.5} color={rows ? '#EB1478' : '#9CA3AF'} style={{ margin:'0 auto 10px' }}/>
          {rows
            ? <div style={{ fontSize:13, fontWeight:600, color:'#EB1478' }}>✓ {rows.length} row{rows.length !== 1 ? 's' : ''} loaded</div>
            : <div style={{ fontSize:13, color:'#6B7280' }}>Drop your Indiegogo CSV here or <span style={{ color:'#EB1478', fontWeight:600 }}>browse</span></div>
          }
          {rows && <div style={{ fontSize:12, color:'#9CA3AF', marginTop:4 }}>Click to replace</div>}
        </div>
        <input ref={fileInputRef} type="file" accept=".csv" style={{ display:'none' }} onChange={e => handleFile(e.target.files[0])}/>

        {detectedCols.length > 0 && (
          <div style={{ marginBottom:12, padding:'8px 12px', background:'#F9FAFB', border:'1px solid #F3F4F6', borderRadius:8, fontSize:11, color:'#6B7280' }}>
            <span style={{ fontWeight:600, color:'#374151' }}>Detected columns: </span>
            {detectedCols.join(', ')}
          </div>
        )}

        {preview.length > 0 && (
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:11, fontWeight:700, color:'#9CA3AF', letterSpacing:'0.08em', marginBottom:8 }}>PREVIEW (first {preview.length} rows)</div>
            <div style={{ border:'1px solid #E5E7EB', borderRadius:8, overflow:'hidden' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                <thead>
                  <tr style={{ background:'#F9FAFB' }}>
                    {['Order ID','Name','Email','City','Country','Amount','Date'].map(h => (
                      <th key={h} style={{ padding:'6px 10px', textAlign:'left', fontSize:10, fontWeight:600, color:'#9CA3AF', borderBottom:'1px solid #E5E7EB', whiteSpace:'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.map((r, i) => (
                    <tr key={i} style={{ borderBottom: i < preview.length-1 ? '1px solid #F3F4F6' : 'none' }}>
                      <td style={{ padding:'6px 10px', fontFamily:'monospace', color:'#374151', fontSize:11 }}>{r.id || '-'}</td>
                      <td style={{ padding:'6px 10px' }}>{[r.firstName, r.lastName].filter(Boolean).join(' ') || '-'}</td>
                      <td style={{ padding:'6px 10px', color:'#6B7280', maxWidth:110, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.email || '-'}</td>
                      <td style={{ padding:'6px 10px', color:'#9CA3AF' }}>{r.city || '-'}</td>
                      <td style={{ padding:'6px 10px', color:'#9CA3AF' }}>{r.country || '-'}</td>
                      <td style={{ padding:'6px 10px', fontFamily:'monospace', fontWeight:600, color: r.amount == null ? '#EF4444' : '#1A1A2E' }}>{r.amount != null ? `$${r.amount}` : '?'}</td>
                      <td style={{ padding:'6px 10px', color: r.date ? '#9CA3AF' : '#EF4444', fontSize:11 }}>{r.date || '?'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {error && <div style={{ marginBottom:12, padding:'8px 12px', background:'#FEE2E2', borderRadius:8, fontSize:12, color:'#991B1B' }}>{error}</div>}

        {result && (
          <div style={{ marginBottom:12, padding:'8px 12px', background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:8, fontSize:12, color:'#065F46' }}>
            ✓ Imported {result.inserted} new orders ({result.total} rows processed)
          </div>
        )}

        <button onClick={runImport} disabled={loading || !rows} style={{
          width:'100%', background: (!rows || loading) ? '#F7A8D4' : '#EB1478',
          color:'#fff', border:'none', borderRadius:8, padding:'10px',
          fontSize:13, fontWeight:600, cursor: (!rows || loading) ? 'not-allowed' : 'pointer',
        }}>
          {loading ? 'Importing…' : rows ? `Import ${rows.length} orders` : 'Upload a CSV first'}
        </button>
      </div>
    </>
  );
};

const GamefoundCsvModal = ({ onClose, onImported }) => {
  const [rows, setRows]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [result, setResult]     = useState(null);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef();

  const parseCsvLine = (line) => {
    const out = []; let cur = ''; let inQ = false;
    for (let i = 0; i < line.length; i++) {
      if (line[i] === '"') {
        if (inQ && line[i+1] === '"') { cur += '"'; i++; }
        else inQ = !inQ;
      } else if (line[i] === ',' && !inQ) { out.push(cur); cur = ''; }
      else cur += line[i];
    }
    out.push(cur);
    return out;
  };

  const parseCsv = (text) => {
    const clean = text.replace(/^﻿/, '');
    const lines = clean.trim().split(/\r?\n/);
    const headers = parseCsvLine(lines[0]).map(h => h.trim());
    return lines.slice(1).filter(l => l.trim()).map(line => {
      const vals = parseCsvLine(line);
      const obj = {};
      headers.forEach((h, i) => { obj[h] = (vals[i] || '').trim(); });
      return obj;
    });
  };

  const col = (row, ...names) => {
    const keys = Object.keys(row);
    for (const name of names) {
      const lo = name.toLowerCase();
      const found = keys.find(k => k.toLowerCase() === lo);
      if (found !== undefined && row[found] !== '') return row[found];
    }
    return '';
  };

  const colContains = (row, ...substrings) => {
    const keys = Object.keys(row);
    for (const sub of substrings) {
      const lo = sub.toLowerCase();
      const found = keys.find(k => k.toLowerCase().includes(lo));
      if (found !== undefined && row[found] !== '') return row[found];
    }
    return '';
  };

  const normalizeDate = (raw) => {
    if (!raw) return '';
    const isoMatch = raw.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoMatch) return isoMatch[1];
    const ymdSlash = raw.match(/^(\d{4})\/(\d{2})\/(\d{2})/);
    if (ymdSlash) return `${ymdSlash[1]}-${ymdSlash[2]}-${ymdSlash[3]}`;
    const usMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (usMatch) return `${usMatch[3]}-${usMatch[1].padStart(2,'0')}-${usMatch[2].padStart(2,'0')}`;
    return raw.split(/[T ]/)[0];
  };

  const mapRow = (row, idx) => {
    const firstName = col(row, 'First name', 'First Name');
    const lastName  = col(row, 'Last name',  'Last Name');
    const combined  = (!firstName && !lastName)
      ? (col(row, 'Backer name', 'Name', 'Full Name') || '').trim() : '';
    const parts     = combined.split(/\s+/);
    const first     = firstName || parts[0] || '';
    const last      = lastName  || parts.slice(1).join(' ') || '';
    const amountRaw = col(row, 'Total pledge', 'Pledge amount', 'Amount charged', 'Amount', 'Total')
      || colContains(row, 'total pledge', 'pledge amount', 'amount charged', 'amount');
    const dateRaw   = col(row, 'Order date', 'Pledged at', 'Pledge date', 'Date')
      || colContains(row, 'order date', 'pledged at', 'pledge date');
    const rawId     = col(row, 'Order #', 'Order number', 'Backer number', 'ID');
    const amountStr = amountRaw.replace(/[^0-9.]/g, '');
    const parsed    = parseFloat(amountStr);
    const amountKey = Object.keys(row).find(k => /total.?pledge|pledge.?amount|amount.?charged/i.test(k)) || '';
    const currMatch = amountKey.match(/\(([A-Z]{3})\)/i);
    const currency  = currMatch ? currMatch[1].toLowerCase() : 'usd';
    return {
      source_id:    rawId ? `gf-${rawId}` : `gf-row-${idx + 1}`,
      source:       'gamefound',
      firstName:    first,
      lastName:     last,
      email:        col(row, 'Email'),
      phone:        col(row, 'Phone'),
      addressLine1: col(row, 'Shipping address', 'Street', 'Address', 'Address 1'),
      city:         col(row, 'City'),
      state:        col(row, 'State', 'Province', 'Region'),
      postalCode:   col(row, 'ZIP', 'Zip', 'Postal code', 'Post code'),
      country:      col(row, 'Country'),
      product:      col(row, 'Reward tier', 'Reward', 'Tier', 'Pledge tier'),
      qty:          1,
      amount:       isNaN(parsed) ? null : parsed.toFixed(2),
      currency,
      status:       col(row, 'Order status', 'Payment status', 'Status') || colContains(row, 'status') || 'paid',
      date:         normalizeDate(dateRaw),
    };
  };

  const handleFile = (f) => {
    if (!f || !f.name.endsWith('.csv')) { setError('Please upload a .csv file'); return; }
    setError(''); setResult(null); setRows(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try { setRows(parseCsv(e.target.result)); }
      catch (err) { setError('Failed to parse CSV: ' + err.message); }
    };
    reader.readAsText(f);
  };

  const runImport = async () => {
    if (!rows || rows.length === 0) { setError('No rows found in CSV'); return; }
    const orders = rows.map((r, i) => mapRow(r, i)).filter(o => o.source_id || o.email);
    if (orders.length === 0) { setError('Could not map any valid orders - check your CSV columns'); return; }
    setLoading(true); setError('');
    const { ok, data } = await api('POST', '/api/orders/import', { orders });
    setLoading(false);
    if (!ok) { setError(data.error || 'Import failed'); return; }
    setResult(data);
    onImported();
  };

  const preview      = rows ? rows.slice(0, 3).map((r, i) => mapRow(r, i)) : [];
  const detectedCols = rows && rows[0] ? Object.keys(rows[0]) : [];

  const dropZoneStyle = {
    border: `2px dashed ${dragging ? '#2BA84A' : '#D1D5DB'}`,
    borderRadius:10, padding:'28px 20px', textAlign:'center', cursor:'pointer',
    background: dragging ? '#E3F5EA' : '#F9FAFB', transition:'all 0.15s', marginBottom:16,
  };

  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.25)', zIndex:40 }}/>
      <div style={{ position:'fixed', top:'50%', left:'50%', transform:'translate(-50%,-50%)', zIndex:50, background:'radial-gradient(circle at 12% 0%, rgba(0,111,221,0.08), transparent 34%), radial-gradient(circle at 88% 0%, rgba(115,208,75,0.10), transparent 30%), rgba(255,255,255,0.96)', border:'1px solid rgba(199,213,230,0.9)', backdropFilter:'blur(12px)', WebkitBackdropFilter:'blur(12px)', borderRadius:16, padding:'28px 32px', width:520, maxHeight:'85vh', overflowY:'auto', boxShadow:'0 8px 40px rgba(0,0,0,0.16)' }}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:22 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ width:32, height:32, borderRadius:8, background:'#E3F5EA', display:'flex', alignItems:'center', justifyContent:'center', padding:6 }}>
              <img src="/assets/payment_processors/logo/gamefound.webp" alt="" style={{ width:'100%', height:'100%', objectFit:'contain' }}/>
            </div>
            <span style={{ fontWeight:700, fontSize:15 }}>Import Gamefound CSV</span>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, color:'#9CA3AF', cursor:'pointer', lineHeight:1 }}>×</button>
        </div>

        <div
          style={dropZoneStyle}
          onClick={() => fileInputRef.current.click()}
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
        >
          <Upload size={24} strokeWidth={1.5} color={rows ? '#2BA84A' : '#9CA3AF'} style={{ margin:'0 auto 10px' }}/>
          {rows
            ? <div style={{ fontSize:13, fontWeight:600, color:'#2BA84A' }}>✓ {rows.length} row{rows.length !== 1 ? 's' : ''} loaded</div>
            : <div style={{ fontSize:13, color:'#6B7280' }}>Drop your Gamefound CSV here or <span style={{ color:'#2BA84A', fontWeight:600 }}>browse</span></div>
          }
          {rows && <div style={{ fontSize:12, color:'#9CA3AF', marginTop:4 }}>Click to replace</div>}
        </div>
        <input ref={fileInputRef} type="file" accept=".csv" style={{ display:'none' }} onChange={e => handleFile(e.target.files[0])}/>

        {detectedCols.length > 0 && (
          <div style={{ marginBottom:12, padding:'8px 12px', background:'#F9FAFB', border:'1px solid #F3F4F6', borderRadius:8, fontSize:11, color:'#6B7280' }}>
            <span style={{ fontWeight:600, color:'#374151' }}>Detected columns: </span>
            {detectedCols.join(', ')}
          </div>
        )}

        {preview.length > 0 && (
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:11, fontWeight:700, color:'#9CA3AF', letterSpacing:'0.08em', marginBottom:8 }}>PREVIEW (first {preview.length} rows)</div>
            <div style={{ border:'1px solid #E5E7EB', borderRadius:8, overflow:'hidden' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                <thead>
                  <tr style={{ background:'#F9FAFB' }}>
                    {['Order ID','Name','Email','City','Country','Amount','Date'].map(h => (
                      <th key={h} style={{ padding:'6px 10px', textAlign:'left', fontSize:10, fontWeight:600, color:'#9CA3AF', borderBottom:'1px solid #E5E7EB', whiteSpace:'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.map((r, i) => (
                    <tr key={i} style={{ borderBottom: i < preview.length-1 ? '1px solid #F3F4F6' : 'none' }}>
                      <td style={{ padding:'6px 10px', fontFamily:'monospace', color:'#374151', fontSize:11 }}>{r.id || '-'}</td>
                      <td style={{ padding:'6px 10px' }}>{[r.firstName, r.lastName].filter(Boolean).join(' ') || '-'}</td>
                      <td style={{ padding:'6px 10px', color:'#6B7280', maxWidth:110, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.email || '-'}</td>
                      <td style={{ padding:'6px 10px', color:'#9CA3AF' }}>{r.city || '-'}</td>
                      <td style={{ padding:'6px 10px', color:'#9CA3AF' }}>{r.country || '-'}</td>
                      <td style={{ padding:'6px 10px', fontFamily:'monospace', fontWeight:600, color: r.amount == null ? '#EF4444' : '#1A1A2E' }}>{r.amount != null ? `$${r.amount}` : '?'}</td>
                      <td style={{ padding:'6px 10px', color: r.date ? '#9CA3AF' : '#EF4444', fontSize:11 }}>{r.date || '?'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {error && <div style={{ marginBottom:12, padding:'8px 12px', background:'#FEE2E2', borderRadius:8, fontSize:12, color:'#991B1B' }}>{error}</div>}

        {result && (
          <div style={{ marginBottom:12, padding:'8px 12px', background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:8, fontSize:12, color:'#065F46' }}>
            ✓ Imported {result.inserted} new orders ({result.total} rows processed)
          </div>
        )}

        <button onClick={runImport} disabled={loading || !rows} style={{
          width:'100%', background: (!rows || loading) ? '#A8D9B5' : '#2BA84A',
          color:'#fff', border:'none', borderRadius:8, padding:'10px',
          fontSize:13, fontWeight:600, cursor: (!rows || loading) ? 'not-allowed' : 'pointer',
        }}>
          {loading ? 'Importing…' : rows ? `Import ${rows.length} orders` : 'Upload a CSV first'}
        </button>
      </div>
    </>
  );
};

const StripeImportModal = ({ onClose, onImported }) => {
  const [mode, setMode]       = useState('checkout_sessions');
  const [linkId, setLinkId]   = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [result, setResult]   = useState(null);

  const runImport = async () => {
    if (mode === 'payment_link' && !linkId.startsWith('plink_')) {
      setError('Payment link ID must start with plink_'); return;
    }
    setLoading(true);
    setError('');
    setResult(null);
    const { ok, data } = await api('POST', '/api/stripe/sync', { mode, paymentLinkId: linkId || undefined });
    setLoading(false);
    if (!ok) { setError(data.error || 'Import failed'); return; }
    setResult(data);
    onImported();
  };

  const radio = (val) => ({
    display:'flex', alignItems:'center', gap:10, padding:'10px 14px', borderRadius:8,
    border: `1.5px solid ${mode===val ? '#006FDD' : '#E5E7EB'}`,
    background: mode===val ? '#EEF4FF' : '#fff', cursor:'pointer', marginBottom:8,
  });

  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.25)', zIndex:40 }}/>
      <div style={{ position:'fixed', top:'50%', left:'50%', transform:'translate(-50%,-50%)', zIndex:50, background:'radial-gradient(circle at 12% 0%, rgba(0,111,221,0.08), transparent 34%), radial-gradient(circle at 88% 0%, rgba(115,208,75,0.10), transparent 30%), rgba(255,255,255,0.96)', border:'1px solid rgba(199,213,230,0.9)', backdropFilter:'blur(12px)', WebkitBackdropFilter:'blur(12px)', borderRadius:16, padding:'28px 32px', width:460, boxShadow:'0 8px 40px rgba(0,0,0,0.16)' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:22 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ width:32, height:32, borderRadius:8, background:'#F0EFFE', display:'flex', alignItems:'center', justifyContent:'center', padding:6 }}>
              <img src="/assets/payment_processors/logo/stripe.webp" alt="" style={{ width:'100%', objectFit:'contain' }}/>
            </div>
            <span style={{ fontWeight:700, fontSize:15 }}>Import from Stripe</span>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, color:'#9CA3AF', cursor:'pointer', lineHeight:1 }}>×</button>
        </div>

        <div style={{ marginBottom:16 }}>
          <div style={{ fontSize:12, fontWeight:600, color:'#374151', marginBottom:10 }}>Import from</div>
          <div onClick={() => setMode('checkout_sessions')} style={radio('checkout_sessions')}>
            <div style={{ width:16, height:16, borderRadius:'50%', border:`2px solid ${mode==='checkout_sessions'?'#006FDD':'#D1D5DB'}`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              {mode==='checkout_sessions' && <div style={{ width:8, height:8, borderRadius:'50%', background:'#006FDD' }}/>}
            </div>
            <div>
              <div style={{ fontSize:13, fontWeight:600 }}>Whole account</div>
              <div style={{ fontSize:11, color:'#9CA3AF' }}>Import all completed checkout orders (includes product names)</div>
            </div>
          </div>
          <div onClick={() => setMode('payment_link')} style={radio('payment_link')}>
            <div style={{ width:16, height:16, borderRadius:'50%', border:`2px solid ${mode==='payment_link'?'#006FDD':'#D1D5DB'}`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              {mode==='payment_link' && <div style={{ width:8, height:8, borderRadius:'50%', background:'#006FDD' }}/>}
            </div>
            <div>
              <div style={{ fontSize:13, fontWeight:600 }}>Specific payment link</div>
              <div style={{ fontSize:11, color:'#9CA3AF' }}>Import only from a plink_… ID</div>
            </div>
          </div>
        </div>

        {mode === 'payment_link' && (
          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:12, fontWeight:600, color:'#374151', display:'block', marginBottom:6 }}>Payment link ID</label>
            <input value={linkId} onChange={e => setLinkId(e.target.value)}
              placeholder="plink_…" style={{ width:'100%', border:'1px solid #E5E7EB', borderRadius:8, padding:'9px 12px', fontSize:13, outline:'none', background:'#fff' }}/>
          </div>
        )}

        {error && <div style={{ marginBottom:12, padding:'8px 12px', background:'#FEE2E2', borderRadius:8, fontSize:12, color:'#991B1B' }}>{error}</div>}

        {result && (
          <div style={{ marginBottom:12, padding:'8px 12px', background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:8, fontSize:12, color:'#065F46' }}>
            ✓ Imported {result.inserted} new orders ({result.total} fetched from Stripe)
          </div>
        )}

        <button onClick={runImport} disabled={loading} style={{ width:'100%', background: loading ? '#A5B4FC' : '#635BFF', color:'#fff', border:'none', borderRadius:8, padding:'10px', fontSize:13, fontWeight:600, cursor: loading ? 'not-allowed' : 'pointer' }}>
          {loading ? 'Importing…' : 'Run import'}
        </button>
      </div>
    </>
  );
};

const PayPalSyncModal = ({ onClose, onImported }) => {
  const [loading, setLoading] = useState('');
  const [result,  setResult]  = useState(null);
  const [error,   setError]   = useState('');

  const sync = async (backfill = false) => {
    setLoading(backfill ? 'backfill' : 'new'); setError(''); setResult(null);
    const { ok, data } = await api('POST', '/api/paypal/sync', backfill ? { backfill: true } : {});
    setLoading('');
    if (ok) { setResult(data); onImported?.(data); }
    else    setError(data.error || 'Sync failed');
  };

  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.25)', zIndex:60 }}/>
      <div style={{ position:'fixed', top:'50%', left:'50%', transform:'translate(-50%,-50%)', background:'radial-gradient(circle at 12% 0%, rgba(0,111,221,0.08), transparent 34%), radial-gradient(circle at 88% 0%, rgba(115,208,75,0.10), transparent 30%), rgba(255,255,255,0.96)', border:'1px solid rgba(199,213,230,0.9)', backdropFilter:'blur(12px)', WebkitBackdropFilter:'blur(12px)', borderRadius:14, width:360, padding:28, boxShadow:'0 16px 48px rgba(0,0,0,0.15)', zIndex:61 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ width:36, height:36, borderRadius:9, background:'#EEF6FF', display:'flex', alignItems:'center', justifyContent:'center', padding:7 }}>
              <img src="/assets/payment_processors/logo/paypal.webp" alt="" style={{ width:'100%', height:'100%', objectFit:'contain' }}/>
            </div>
            <span style={{ fontWeight:700, fontSize:15 }}>Sync PayPal Orders</span>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#9CA3AF', lineHeight:1 }}>×</button>
        </div>

        {!result ? (
          <>
            <div style={{ fontSize:12, color:'#6B7280', lineHeight:1.45, marginBottom:14 }}>
              Omnifill imports completed incoming PayPal sales. Backfills scan PayPal's available 3-year history on the server in 31-day API windows.
            </div>
            {error && <div style={{ fontSize:12, color:'#991B1B', background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:7, padding:'8px 12px', marginBottom:14 }}>{error}</div>}
            <button onClick={() => sync(false)} disabled={!!loading}
              style={{ width:'100%', background:loading ? '#9CA3AF' : '#111827', color:'#fff', border:'1px solid ' + (loading ? '#9CA3AF' : '#111827'), borderRadius:8, padding:'10px', fontSize:14, fontWeight:700, cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.75 : 1 }}>
              {loading === 'new' ? 'Syncing...' : 'Sync new sales'}
            </button>
            <button onClick={() => sync(true)} disabled={!!loading}
              style={{ marginTop:10, width:'100%', background:'#fff', color:'#374151', border:'1px solid #D1D5DB', borderRadius:8, padding:'10px', fontSize:13, fontWeight:700, cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.7 : 1 }}>
              {loading === 'backfill' ? 'Backfilling...' : 'Backfill all available history'}
            </button>
          </>
        ) : (
          <div style={{ textAlign:'center', padding:'8px 0' }}>
            <div style={{ fontSize:36, fontWeight:800, color:'#0070BA', marginBottom:6 }}>{result.inserted}</div>
            <div style={{ fontSize:13, color:'#6B7280' }}>new orders imported of {result.total} PayPal sales found</div>
            <div style={{ fontSize:11, color:'#9CA3AF', lineHeight:1.45, marginTop:8 }}>
              Scanned {result.scanned || result.total || 0} transactions across {result.windows || 0} API window{result.windows === 1 ? '' : 's'}.
              {result.cleaned ? ` Hidden ${result.cleaned} outgoing payment${result.cleaned === 1 ? '' : 's'} from previous imports.` : ''}
            </div>
            <button onClick={onClose} style={{ marginTop:20, width:'100%', background:'#F3F4F6', color:'#374151', border:'none', borderRadius:8, padding:'9px', fontSize:13, fontWeight:600, cursor:'pointer' }}>
              Done
            </button>
          </div>
        )}
      </div>
    </>
  );
};


const relativeTime = (iso) => {
  if (!iso) return 'never';
  const normalizedIso = /(?:Z|[+-]\d{2}:\d{2})$/i.test(String(iso)) ? iso : `${iso}Z`;
  const timestamp = new Date(normalizedIso).getTime();
  if (!Number.isFinite(timestamp)) return 'never';
  const diff = Date.now() - timestamp;
  const min  = Math.floor(diff / 60000);
  if (min < 1)  return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24)  return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
};

const StoreConnectModal = ({ source, onClose }) => {
  const isShopify = source === 'shopify';
  const normalizeShopifyInput = raw => {
    let shop = String(raw || '').trim().toLowerCase();
    shop = shop.replace(/^https?:\/\//i, '').replace(/\/.*$/, '').replace(/^www\./, '');
    if (!shop.endsWith('.myshopify.com') && shop.includes('.')) shop = shop.split('.')[0];
    if (!shop.endsWith('.myshopify.com')) shop = `${shop}.myshopify.com`;
    return /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/i.test(shop) ? shop : '';
  };
  const cfg = isShopify
    ? {
        name:'Shopify',
        color:'#008060',
        bg:'#E8F5EF',
        logo:'/assets/payment_processors/logo/shopify.webp',
        label:'Store domain',
        placeholder:'e.g. coolbrand.myshopify.com',
        helper:'You can also enter coolbrand or coolbrand.com and Omnifill will try coolbrand.myshopify.com.',
        path:'/api/shopify/connect',
        param:'shop',
      }
    : {
        name:'WooCommerce',
        color:'#7F54B3',
        bg:'#F3EEF9',
        logo:'/assets/payment_processors/logo/woocommerce.webp',
        label:'Store URL',
        placeholder:'https://example.com',
        helper:'Use the public WordPress store URL where WooCommerce is installed.',
        path:'/api/woocommerce/connect',
        param:'store',
      };
  const [value, setValue] = useState('');
  const [error, setError] = useState('');

  const submit = e => {
    e.preventDefault();
    const trimmed = value.trim();
    if (!trimmed) {
      setError(isShopify ? 'Enter a Shopify store domain.' : 'Enter a WooCommerce store URL.');
      return;
    }
    const connectValue = isShopify ? normalizeShopifyInput(trimmed) : trimmed;
    if (isShopify && !connectValue) {
      setError('Use a valid Shopify domain, like coolbrand.myshopify.com or coolbrand.com.');
      return;
    }
    window.location.href = `${cfg.path}?${cfg.param}=${encodeURIComponent(connectValue)}`;
  };

  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(17,24,39,0.42)', zIndex:1000 }}/>
      <div role="dialog" aria-modal="true" aria-label={`Connect ${cfg.name}`} style={{ position:'fixed', top:'50%', left:'50%', transform:'translate(-50%, -50%)', width:'min(400px, calc(100vw - 32px))', background:'radial-gradient(circle at 12% 0%, rgba(0,111,221,0.08), transparent 34%), radial-gradient(circle at 88% 0%, rgba(115,208,75,0.10), transparent 30%), rgba(255,255,255,0.96)', border:'1px solid rgba(199,213,230,0.9)', backdropFilter:'blur(12px)', WebkitBackdropFilter:'blur(12px)', borderRadius:10, boxShadow:'0 22px 54px rgba(15,23,42,0.20)', padding:18, zIndex:1001 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12, marginBottom:14 }}>
          <div style={{ display:'flex', gap:12, alignItems:'center' }}>
            <div style={{ width:38, height:38, borderRadius:8, background:cfg.bg, display:'flex', alignItems:'center', justifyContent:'center', padding:6 }}>
              <img src={cfg.logo} alt="" style={{ width:'100%', height:'100%', objectFit:'contain' }}/>
            </div>
            <div>
              <div style={{ fontSize:15, fontWeight:800, color:'#111827' }}>Connect {cfg.name}</div>
              <div style={{ fontSize:12, color:'#6B7280', marginTop:3 }}>Omnifill will send you to {cfg.name} to approve access.</div>
            </div>
          </div>
          <button onClick={onClose} title="Close" style={{ background:'none', border:'none', color:'#9CA3AF', cursor:'pointer', display:'flex', padding:3 }}>
            <X size={18} strokeWidth={2.2}/>
          </button>
        </div>

        <form onSubmit={submit}>
          <label style={{ display:'block', fontSize:12, fontWeight:700, color:'#374151', marginBottom:6 }}>{cfg.label}</label>
          <input
            autoFocus
            value={value}
            onChange={e => { setValue(e.target.value); setError(''); }}
            placeholder={cfg.placeholder}
            style={{ width:'100%', border:'1px solid #D1D5DB', borderRadius:8, padding:'10px 12px', fontSize:14, outline:'none', color:'#111827', background:'#fff' }}
          />
          <div style={{ fontSize:12, color:'#6B7280', marginTop:7, lineHeight:1.4 }}>{cfg.helper}</div>
          {error && (
            <div style={{ marginTop:12, background:'#FEF2F2', border:'1px solid #FECACA', color:'#991B1B', borderRadius:8, padding:'9px 11px', fontSize:12 }}>
              {error}
            </div>
          )}
          <div style={{ display:'flex', justifyContent:'flex-end', gap:8, marginTop:16 }}>
            <button type="button" onClick={onClose} style={{ background:'#fff', color:'#374151', border:'1px solid #D1D5DB', borderRadius:8, padding:'9px 13px', fontSize:13, fontWeight:700, cursor:'pointer' }}>
              Cancel
            </button>
            <button type="submit" style={{ background:'#111827', color:'#fff', border:'1px solid #111827', borderRadius:8, padding:'9px 14px', fontSize:13, fontWeight:800, cursor:'pointer' }}>
              Continue
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

const AssistedSourceConnectModal = ({ source, onClose, onOpenSupport }) => {
  const cfg = PLATFORM_CFG[source?.id] || source || {};

  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(17,24,39,0.42)', zIndex:1000 }}/>
      <div role="dialog" aria-modal="true" aria-label={`Connect ${source.name}`} style={{ position:'fixed', top:'50%', left:'50%', transform:'translate(-50%, -50%)', width:'min(400px, calc(100vw - 32px))', background:'radial-gradient(circle at 12% 0%, rgba(0,111,221,0.08), transparent 34%), radial-gradient(circle at 88% 0%, rgba(115,208,75,0.10), transparent 30%), rgba(255,255,255,0.96)', border:'1px solid rgba(199,213,230,0.9)', backdropFilter:'blur(12px)', WebkitBackdropFilter:'blur(12px)', borderRadius:10, boxShadow:'0 22px 54px rgba(15,23,42,0.20)', padding:18, zIndex:1001 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12, marginBottom:14 }}>
          <div style={{ display:'flex', gap:12, alignItems:'center' }}>
            <div style={{ width:38, height:38, borderRadius:8, background:cfg.bg || '#F3F4F6', display:'flex', alignItems:'center', justifyContent:'center', padding:6 }}>
              {cfg.logo
                ? <img src={cfg.logo} alt="" style={{ width:'100%', height:'100%', objectFit:'contain' }}/>
                : <span style={{ fontSize:13, fontWeight:900, color:cfg.color || '#111827' }}>{cfg.char || source.name[0]}</span>
              }
            </div>
            <div>
              <div style={{ fontSize:15, fontWeight:800, color:'#111827' }}>Connect {source.name}</div>
              <div style={{ fontSize:12, color:'#6B7280', marginTop:3 }}>Omnifill support will help configure this source for your account.</div>
            </div>
          </div>
          <button onClick={onClose} title="Close" style={{ background:'none', border:'none', color:'#9CA3AF', cursor:'pointer', display:'flex', padding:3 }}>
            <X size={18} strokeWidth={2.2}/>
          </button>
        </div>

        <div style={{ border:'1px solid #E5E7EB', borderRadius:8, padding:10, background:'#F9FAFB', fontSize:12, color:'#374151', lineHeight:1.45 }}>
          Open a support ticket and include your store URL, marketplace account email, and any export sample you already use.
        </div>

        <div style={{ display:'flex', justifyContent:'flex-end', gap:8, marginTop:16 }}>
          <button type="button" onClick={onClose} style={{ background:'#fff', color:'#374151', border:'1px solid #D1D5DB', borderRadius:8, padding:'9px 13px', fontSize:13, fontWeight:700, cursor:'pointer' }}>
            Cancel
          </button>
          <button type="button" onClick={() => onOpenSupport(source)} style={{ background:'#111827', color:'#fff', border:'1px solid #111827', borderRadius:8, padding:'9px 14px', fontSize:13, fontWeight:800, cursor:'pointer' }}>
            Open Support
          </button>
        </div>
      </div>
    </>
  );
};

const AdobeCommerceUrlBox = ({ label, value }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {}
  };
  return (
    <div style={{ border:'1px solid #E5E7EB', borderRadius:8, background:'#fff', padding:10 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8, marginBottom:6 }}>
        <div style={{ fontSize:12, fontWeight:800, color:'#374151' }}>{label}</div>
        <button type="button" onClick={copy} disabled={!value} title={`Copy ${label}`} style={{ background:'#F9FAFB', color:'#374151', border:'1px solid #E5E7EB', borderRadius:7, width:31, height:29, display:'inline-flex', alignItems:'center', justifyContent:'center', cursor:value ? 'pointer' : 'not-allowed', opacity:value ? 1 : 0.55 }}>
          {copied ? <Check size={14} strokeWidth={2.4}/> : <Copy size={14} strokeWidth={2.2}/>}
        </button>
      </div>
      <div style={{ fontFamily:'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize:11.5, lineHeight:1.45, color:'#111827', wordBreak:'break-all' }}>
        {value || 'Generating setup URL...'}
      </div>
    </div>
  );
};

const AdobeCommerceSetupScreen = ({ onNavigate }) => {
  const connectionId = window.location.pathname.match(/\/connectors\/adobe-commerce\/setup\/(\d+)/)?.[1] || '';
  const [status, setStatus] = useState(null);
  const [storeUrl, setStoreUrl] = useState('');
  const [storeUrlSaving, setStoreUrlSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [startingOver, setStartingOver] = useState(false);

  const loadStatus = useCallback(async () => {
    if (!connectionId) return;
    const { ok, data } = await api('GET', `/api/connectors/adobe-commerce/setup/${connectionId}/status`);
    if (ok) {
      setStatus(data);
      setStoreUrl(current => current || data.storeUrl || '');
      if (data.state === 'ACTIVE') {
        setMessage('Connected. Redirecting to Sources...');
        setTimeout(() => {
          history.pushState({}, '', '/sources');
          onNavigate?.('sources');
        }, 2000);
      }
    } else {
      setError(data?.error || 'Could not load setup status.');
    }
  }, [connectionId, onNavigate]);

  useEffect(() => {
    loadStatus();
    const id = setInterval(loadStatus, 3000);
    return () => clearInterval(id);
  }, [loadStatus]);

  const saveStoreUrl = async e => {
    e.preventDefault();
    setError('');
    setMessage('');
    setStoreUrlSaving(true);
    const { ok, data } = await api('POST', `/api/connectors/adobe-commerce/setup/${connectionId}/store-url`, { storeUrl });
    setStoreUrlSaving(false);
    if (ok) {
      setStatus(data);
      setMessage('Store URL saved. Keep this tab open while you activate the integration in Magento.');
    } else {
      setError(data?.error || 'Could not save Store URL.');
    }
  };

  const startOver = async () => {
    setStartingOver(true);
    setError('');
    setMessage('');
    const { ok, data } = await api('POST', `/api/connectors/adobe-commerce/setup/${connectionId}/start-over`, {});
    setStartingOver(false);
    if (ok) {
      setStatus(data);
      setStoreUrl(data.storeUrl || '');
      setMessage('New setup links generated.');
    } else {
      setError(data?.error || 'Could not restart setup.');
    }
  };

  const state = status?.state || 'PENDING_SETUP';
  const stateLabel = state === 'ACTIVE'
    ? 'Connected'
    : state === 'FAILED'
      ? 'Failed'
      : state === 'AWAITING_ACTIVATION'
        ? 'Awaiting activation'
        : 'Pending setup';
  const statusColor = state === 'ACTIVE' ? '#047857' : state === 'FAILED' ? '#B42318' : '#1D4ED8';
  const callbackUrl = status?.callbackUrl || '';
  const identityLinkUrl = status?.identityLinkUrl || '';

  return (
    <div className="dash-page-scroll workspace-page">
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:14, marginBottom:14, flexWrap:'wrap' }}>
        <div>
          <h1 style={{ margin:0, fontSize:20, fontWeight:900, color:'#111827' }}>Connect Adobe Commerce</h1>
          <p style={{ margin:'4px 0 0', fontSize:13, color:'#6B7280' }}>Use Magento integration activation so tokens never need to be copied by hand.</p>
        </div>
        <button type="button" onClick={() => onNavigate?.('sources')} style={{ background:'#fff', color:'#374151', border:'1px solid #D1D5DB', borderRadius:8, padding:'8px 11px', fontSize:13, fontWeight:800, cursor:'pointer' }}>
          Back to Sources
        </button>
      </div>

      <section className="dash-panel workspace-panel-flat" style={{ marginBottom:10 }}>
        <div style={{ padding:16, display:'grid', gap:14 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <div style={{ width:38, height:38, borderRadius:8, background:'#FEF2F2', display:'flex', alignItems:'center', justifyContent:'center', padding:5 }}>
                <img src="/assets/payment_processors/logo/adobe_commerce.webp" alt="" style={{ width:'100%', height:'100%', objectFit:'contain' }}/>
              </div>
              <div>
                <div style={{ fontSize:14, fontWeight:900, color:'#111827' }}>Adobe Commerce / Magento Open Source</div>
                <div style={{ fontSize:12, color:'#6B7280', marginTop:2 }}>PaaS and on-premise REST API only. ACCS is not supported yet.</div>
              </div>
            </div>
            <div style={{ display:'inline-flex', alignItems:'center', gap:7, color:statusColor, background:state === 'FAILED' ? '#FEF2F2' : state === 'ACTIVE' ? '#F0FDF4' : '#EEF6FF', border:`1px solid ${state === 'FAILED' ? '#FECACA' : state === 'ACTIVE' ? '#BBF7D0' : '#BFDBFE'}`, borderRadius:8, padding:'7px 10px', fontSize:12, fontWeight:900 }}>
              {state === 'FAILED' ? <XCircle size={14} strokeWidth={2.3}/> : state === 'ACTIVE' ? <CheckCircle2 size={14} strokeWidth={2.3}/> : <Clock3 size={14} strokeWidth={2.2}/>}
              {stateLabel}
            </div>
          </div>

          {message && <div style={{ background:'#F0FDF4', border:'1px solid #BBF7D0', color:'#065F46', borderRadius:8, padding:'10px 12px', fontSize:13 }}>{message}</div>}
          {(error || status?.lastError) && state !== 'ACTIVE' && (
            <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', color:'#991B1B', borderRadius:8, padding:'10px 12px', fontSize:13 }}>
              {error || status.lastError}
            </div>
          )}

          {state === 'ACTIVE' ? (
            <div style={{ background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:10, padding:18, color:'#065F46', fontSize:14, fontWeight:800 }}>
              Adobe Commerce is connected.
            </div>
          ) : (
            <>
              <form onSubmit={saveStoreUrl} style={{ display:'grid', gridTemplateColumns:'minmax(0, 1fr) auto', gap:8, alignItems:'end' }}>
                <div>
                  <label style={{ display:'block', fontSize:12, fontWeight:800, color:'#374151', marginBottom:6 }}>Store URL</label>
                  <input value={storeUrl} onChange={e => setStoreUrl(e.target.value)} placeholder="https://store.example.com" style={{ width:'100%', border:'1px solid #D1D5DB', borderRadius:8, padding:'10px 12px', fontSize:14, outline:'none', color:'#111827', background:'#fff' }}/>
                </div>
                <button type="submit" disabled={storeUrlSaving} style={{ background:'#EB1000', color:'#fff', border:'none', borderRadius:8, padding:'10px 13px', fontSize:13, fontWeight:900, cursor:storeUrlSaving ? 'wait' : 'pointer', minHeight:39 }}>
                  {storeUrlSaving ? 'Saving...' : 'Save'}
                </button>
              </form>

              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(260px, 1fr))', gap:10 }}>
                <AdobeCommerceUrlBox label="Callback URL" value={callbackUrl}/>
                <AdobeCommerceUrlBox label="Identity Link URL" value={identityLinkUrl}/>
              </div>

              <div style={{ border:'1px solid #E5E7EB', borderRadius:10, background:'#F9FAFB', padding:14 }}>
                <h2 style={{ margin:'0 0 10px', fontSize:14, fontWeight:900, color:'#111827' }}>Magento admin steps</h2>
                <ol style={{ margin:0, paddingLeft:18, display:'grid', gap:7, fontSize:13, color:'#374151', lineHeight:1.45 }}>
                  <li>Open Magento admin in a new tab and go to System, Extensions, Integrations, Add New Integration.</li>
                  <li>Name it Omnifill, paste the Callback URL and Identity Link URL, then enter your admin password if prompted.</li>
                  <li>On the API tab, choose Custom and grant Sales Operations Orders read access plus Stores Settings Configuration read access.</li>
                  <li>Save, then click Activate on the integration row and Allow in Magento.</li>
                  <li>Return to this tab. The status checks every 3 seconds and will switch to Connected after activation succeeds.</li>
                </ol>
              </div>

              {state === 'FAILED' && (
                <button type="button" onClick={startOver} disabled={startingOver} style={{ justifySelf:'start', background:'#fff', color:'#B42318', border:'1px solid #F3D2CF', borderRadius:8, padding:'9px 12px', fontSize:13, fontWeight:900, cursor:startingOver ? 'wait' : 'pointer' }}>
                  {startingOver ? 'Starting over...' : 'Start over'}
                </button>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

const AdobeCommerceIdentityScreen = ({ onNavigate }) => {
  const params = new URLSearchParams(window.location.search);
  const nonce = params.get('nonce') || '';
  const returnUrl = params.get('success_call_back') || params.get('callback_url') || params.get('return_url') || '';
  const [state, setState] = useState({ loading:true, error:'', data:null, working:false });

  useEffect(() => {
    api('GET', `/api/connectors/adobe-commerce/identity?${new URLSearchParams({ nonce, return_url:returnUrl })}`).then(({ ok, data }) => {
      setState({ loading:false, error: ok ? '' : (data?.error || 'Setup link expired.'), data: ok ? data : null, working:false });
    });
  }, [nonce, returnUrl]);

  const allow = async () => {
    setState(prev => ({ ...prev, working:true, error:'' }));
    const { ok, data } = await api('POST', '/api/connectors/adobe-commerce/identity/allow', { nonce, returnUrl });
    if (ok) {
      if (data.redirectUrl) window.location.href = data.redirectUrl;
      else onNavigate?.('sources');
      return;
    }
    setState(prev => ({ ...prev, working:false, error:data?.error || 'Could not authorize setup.' }));
  };

  const deny = async () => {
    await api('POST', '/api/connectors/adobe-commerce/identity/deny', { nonce });
    onNavigate?.('sources');
  };

  return (
    <div style={{ minHeight:'100%', display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
      <div style={{ width:'100%', maxWidth:430, background:'#fff', border:'1px solid #E5E7EB', borderRadius:10, padding:22, boxShadow:'0 14px 38px rgba(15,23,42,0.08)' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
          <div style={{ width:38, height:38, borderRadius:8, background:'#FEF2F2', display:'flex', alignItems:'center', justifyContent:'center', padding:5 }}>
            <img src="/assets/payment_processors/logo/adobe_commerce.webp" alt="" style={{ width:'100%', height:'100%', objectFit:'contain' }}/>
          </div>
          <div>
            <h1 style={{ margin:0, fontSize:18, fontWeight:900, color:'#111827' }}>Authorize Omnifill</h1>
            <p style={{ margin:'3px 0 0', fontSize:12.5, color:'#6B7280' }}>Allow Omnifill to read orders from this Adobe Commerce store.</p>
          </div>
        </div>
        {state.loading ? (
          <div style={{ color:'#6B7280', fontSize:13 }}>Checking setup link...</div>
        ) : state.error ? (
          <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', color:'#991B1B', borderRadius:8, padding:'10px 12px', fontSize:13 }}>{state.error}</div>
        ) : (
          <>
            <div style={{ background:'#F9FAFB', border:'1px solid #E5E7EB', borderRadius:8, padding:12, fontSize:13, color:'#374151', lineHeight:1.5 }}>
              {state.data?.storeUrl ? `Store: ${state.data.storeUrl}` : 'This setup link is tied to your Omnifill account.'}
            </div>
            <div style={{ display:'flex', justifyContent:'flex-end', gap:8, marginTop:16 }}>
              <button type="button" onClick={deny} disabled={state.working} style={{ background:'#fff', color:'#374151', border:'1px solid #D1D5DB', borderRadius:8, padding:'9px 13px', fontSize:13, fontWeight:800, cursor:state.working ? 'wait' : 'pointer' }}>Deny</button>
              <button type="button" onClick={allow} disabled={state.working} style={{ background:'#EB1000', color:'#fff', border:'none', borderRadius:8, padding:'9px 14px', fontSize:13, fontWeight:900, cursor:state.working ? 'wait' : 'pointer' }}>
                {state.working ? 'Authorizing...' : 'Allow'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const SourcesScreen = ({ onOrdersChanged, onNavigate, orders = [], ordersLoading = false, autoSyncSettings = {}, onAutoSyncSettingsChange }) => {
  const sources = AVAILABLE_ORDER_SOURCES;
  const liveSourceTypes = ['stripe', 'paypal', 'shopify', 'woocommerce', 'adobe_commerce'];
  const sourceAliases = {
    stripe:['stripe'],
    paypal:['paypal'],
    shopify:['shopify'],
    woocommerce:['woocommerce'],
    adobe_commerce:['adobe_commerce'],
    kickstarter:['kickstarter', 'kickstarter_csv'],
    indiegogo:['indiegogo', 'indiegogo_csv'],
    gamefound:['gamefound', 'gamefound_csv'],
    manual_csv:['manual', 'manual_csv', 'csv'],
    amazon:['amazon'],
    etsy:['etsy'],
    square:['square'],
    bigcommerce:['bigcommerce'],
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [stripeConnected, setStripeConnected] = useState(false);
  const [stripeLoading,   setStripeLoading]   = useState(true);
  const [stripeAccountId, setStripeAccountId] = useState('');
  const [stripeLastSyncedAt, setStripeLastSyncedAt] = useState(null);
  const [stripeResyncing, setStripeResyncing] = useState(false);

  const [paypalConnected, setPaypalConnected] = useState(false);
  const [paypalLoading,   setPaypalLoading]   = useState(true);
  const [paypalEmail,     setPaypalEmail]     = useState('');
  const [paypalLastSyncedAt, setPaypalLastSyncedAt] = useState(null);
  const [paypalModal,     setPaypalModal]     = useState(false);

  const [shopifyConnected, setShopifyConnected] = useState(false);
  const [shopifyLoading,   setShopifyLoading]   = useState(true);
  const [shopifyShop,      setShopifyShop]      = useState('');
  const [shopifyLastSyncedAt, setShopifyLastSyncedAt] = useState(null);
  const [shopifySyncing,   setShopifySyncing]   = useState(false);

  const [wooConnected, setWooConnected] = useState(false);
  const [wooLoading,   setWooLoading]   = useState(true);
  const [wooStoreUrl,  setWooStoreUrl]  = useState('');
  const [wooLastSyncedAt, setWooLastSyncedAt] = useState(null);
  const [wooSyncing,   setWooSyncing]   = useState(false);

  const [adobeConnection, setAdobeConnection] = useState(null);
  const [adobeLoading,    setAdobeLoading]    = useState(true);
  const [adobeSyncing,    setAdobeSyncing]    = useState(false);

  const [ksModal,    setKsModal]    = useState(false);
  const [igModal,    setIgModal]    = useState(false);
  const [gfModal,    setGfModal]    = useState(false);
  const [smartCsvModal, setSmartCsvModal] = useState(false);
  const [connectModal, setConnectModal] = useState(null);
  const [assistedSource, setAssistedSource] = useState(null);
  const [banner,     setBanner]     = useState(null);
  const [bannerMsg,  setBannerMsg]  = useState('');
  const [bannerSrc,  setBannerSrc]  = useState('');
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(autoSyncSettings?.enabled !== false);
  const [autoSyncInterval, setAutoSyncInterval] = useState(String(autoSyncSettings?.intervalMinutes || 10));
  const [autoSyncSaving, setAutoSyncSaving] = useState(false);
  const [autoSyncError, setAutoSyncError] = useState('');

  useEffect(() => {
    setAutoSyncEnabled(autoSyncSettings?.enabled !== false);
    setAutoSyncInterval(String(autoSyncSettings?.intervalMinutes || autoSyncSettings?.defaultIntervalMinutes || 10));
    setAutoSyncError('');
  }, [autoSyncSettings?.enabled, autoSyncSettings?.intervalMinutes, autoSyncSettings?.defaultIntervalMinutes]);

  useEffect(() => {
    api('GET', '/api/stripe/status').then(({ ok, data }) => {
      if (ok) {
        setStripeConnected(!!data.connected);
        setStripeAccountId(data.accountId || '');
        setStripeLastSyncedAt(data.lastSyncedAt || null);
      }
      setStripeLoading(false);
    });
    api('GET', '/api/paypal/status').then(({ ok, data }) => {
      if (ok) {
        setPaypalConnected(!!data.connected);
        setPaypalEmail(data.email || '');
        setPaypalLastSyncedAt(data.lastSyncedAt || null);
      }
      setPaypalLoading(false);
    });
    api('GET', '/api/shopify/status').then(({ ok, data }) => {
      if (ok) {
        setShopifyConnected(!!data.connected);
        setShopifyShop(data.shop || '');
        setShopifyLastSyncedAt(data.lastSyncedAt || null);
      }
      setShopifyLoading(false);
    });
    api('GET', '/api/woocommerce/status').then(({ ok, data }) => {
      if (ok) {
        setWooConnected(!!data.connected);
        setWooStoreUrl(data.storeUrl || '');
        setWooLastSyncedAt(data.lastSyncedAt || null);
      }
      setWooLoading(false);
    });
    api('GET', '/api/connectors/adobe-commerce/status').then(({ ok, data }) => {
      if (ok) setAdobeConnection(data);
      setAdobeLoading(false);
    });

    const params = new URLSearchParams(window.location.search);
    const stripeParam = params.get('stripe');
    const paypalParam = params.get('paypal');
    const shopifyParam = params.get('shopify');
    const wooParam = params.get('woocommerce');
    const adobeParam = params.get('adobe_commerce');
    if (stripeParam === 'connected') {
      setBanner('connected'); setBannerSrc('Stripe'); setBannerMsg(''); setStripeConnected(true);
      window.history.replaceState({}, '', window.location.pathname);
    } else if (stripeParam === 'error') {
      setBanner('error'); setBannerSrc('Stripe'); setBannerMsg(params.get('msg') || 'unknown error');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (paypalParam === 'connected') {
      setBanner('connected'); setBannerSrc('PayPal'); setBannerMsg(''); setPaypalConnected(true);
      window.history.replaceState({}, '', window.location.pathname);
    } else if (paypalParam === 'error') {
      setBanner('error'); setBannerSrc('PayPal'); setBannerMsg(params.get('msg') || 'unknown error');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (shopifyParam === 'connected') {
      setBanner('connected'); setBannerSrc('Shopify'); setBannerMsg(''); setShopifyConnected(true);
      window.history.replaceState({}, '', window.location.pathname);
    } else if (shopifyParam === 'error') {
      setBanner('error'); setBannerSrc('Shopify'); setBannerMsg(params.get('msg') || 'unknown error');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (wooParam === 'connected') {
      setBanner('connected'); setBannerSrc('WooCommerce'); setBannerMsg(''); setWooConnected(true);
      window.history.replaceState({}, '', window.location.pathname);
    } else if (wooParam === 'error') {
      setBanner('error'); setBannerSrc('WooCommerce'); setBannerMsg(params.get('msg') || 'unknown error');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (adobeParam === 'connected') {
      setBanner('connected'); setBannerSrc('Adobe Commerce'); setBannerMsg(''); window.history.replaceState({}, '', window.location.pathname);
    } else if (adobeParam === 'error') {
      setBanner('error'); setBannerSrc('Adobe Commerce'); setBannerMsg(params.get('msg') || 'unknown error');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const activeOrders = Array.isArray(orders) ? orders.filter(order => !isIgnoredDashboardOrder(order)) : [];
  const sourceOrderCount = source => {
    const aliases = sourceAliases[source.id] || [source.id];
    return activeOrders.filter(order => aliases.includes(String(order.source || '').toLowerCase())).length;
  };
  const formatOrderCount = count => `${count} imported order${count === 1 ? '' : 's'}`;
  const formatLastSync = value => value ? relativeTime(value) : 'Not synced yet';
  const isLiveSource = source => liveSourceTypes.includes(source.type);
  const isSourceLoading = source => (
    source.type === 'stripe' ? stripeLoading :
    source.type === 'paypal' ? paypalLoading :
    source.type === 'shopify' ? shopifyLoading :
    source.type === 'woocommerce' ? wooLoading :
    source.type === 'adobe_commerce' ? adobeLoading :
    false
  );
  const isSourceConnected = source => (
    source.type === 'stripe' ? stripeConnected :
    source.type === 'paypal' ? paypalConnected :
    source.type === 'shopify' ? shopifyConnected :
    source.type === 'woocommerce' ? wooConnected :
    source.type === 'adobe_commerce' ? adobeConnection?.state === 'ACTIVE' :
    false
  );
  const sourceLastSyncedAt = source => (
    source.type === 'stripe' ? stripeLastSyncedAt :
    source.type === 'paypal' ? paypalLastSyncedAt :
    source.type === 'shopify' ? shopifyLastSyncedAt :
    source.type === 'woocommerce' ? wooLastSyncedAt :
    source.type === 'adobe_commerce' ? adobeConnection?.lastSyncedAt :
    null
  );
  const sourceConnectionDetail = source => {
    if (source.type === 'stripe') return stripeAccountId ? `Stripe account ${stripeAccountId}` : 'Stripe account connected';
    if (source.type === 'paypal') return paypalEmail || 'PayPal account connected';
    if (source.type === 'shopify') return shopifyShop || 'Shopify store connected';
    if (source.type === 'woocommerce') return wooStoreUrl || 'WooCommerce store connected';
    if (source.type === 'adobe_commerce') return adobeConnection?.storeUrl || 'Adobe Commerce store connected';
    return '';
  };
  const sourceSetupNote = source => {
    if (source.type !== 'adobe_commerce' || !adobeConnection?.id || adobeConnection?.state === 'ACTIVE') return '';
    if (adobeConnection?.state === 'FAILED') return adobeConnection.lastError || 'Setup needs attention.';
    return 'Setup started. Continue activation to start syncing orders.';
  };
  const sourceMatchesSearch = source => {
    const query = smartNormalizeText(searchQuery);
    if (!query) return true;
    const haystack = smartNormalizeText([
      source.name,
      source.category,
      source.method,
      source.description,
      source.type,
    ].filter(Boolean).join(' '));
    return haystack.includes(query);
  };

  const connectedSources = sources.filter(source => isLiveSource(source) && isSourceConnected(source) && sourceMatchesSearch(source));
  const connectedCount = sources.filter(source => isLiveSource(source) && isSourceConnected(source)).length;
  const connectionLoading = sources.some(source => isLiveSource(source) && isSourceLoading(source));
  const availableSources = sources.filter(source => {
    if (!sourceMatchesSearch(source)) return false;
    return !isLiveSource(source) || (!isSourceLoading(source) && !isSourceConnected(source));
  });

  const sourceActionBtn = {
    borderRadius:8,
    padding:'8px 10px',
    fontSize:12,
    fontWeight:800,
    cursor:'pointer',
    display:'inline-flex',
    alignItems:'center',
    justifyContent:'center',
    gap:7,
    lineHeight:1.2,
    minHeight:34,
  };
  const primaryActionBtn = (disabled = false) => ({
    ...sourceActionBtn,
    width:'100%',
    background:disabled ? '#9CA3AF' : '#111827',
    color:'#fff',
    border:'1px solid ' + (disabled ? '#9CA3AF' : '#111827'),
    cursor:disabled ? 'not-allowed' : 'pointer',
    opacity:disabled ? 0.72 : 1,
  });
  const secondaryActionBtn = (disabled = false) => ({
    ...sourceActionBtn,
    background:'#fff',
    color:'#374151',
    border:'1px solid #D1D5DB',
    cursor:disabled ? 'not-allowed' : 'pointer',
    opacity:disabled ? 0.65 : 1,
  });
  const dangerActionBtn = {
    ...sourceActionBtn,
    background:'#fff',
    color:'#B42318',
    border:'1px solid #F3D2CF',
  };
  const tagStyle = {
    display:'inline-flex',
    alignItems:'center',
    height:22,
    padding:'0 7px',
    borderRadius:4,
    border:'1px solid #E5E7EB',
    background:'#F9FAFB',
    color:'#4B5563',
    fontSize:11,
    fontWeight:800,
    lineHeight:1,
  };
  const minAutoSyncInterval = Number(autoSyncSettings?.minIntervalMinutes || 5);
  const maxAutoSyncInterval = Number(autoSyncSettings?.maxIntervalMinutes || 1440);
  const savedAutoSyncInterval = Number(autoSyncSettings?.intervalMinutes || autoSyncSettings?.defaultIntervalMinutes || 10);
  const draftAutoSyncInterval = Number.parseInt(autoSyncInterval, 10);
  const autoSyncDirty = autoSyncEnabled !== (autoSyncSettings?.enabled !== false)
    || (Number.isFinite(draftAutoSyncInterval) && draftAutoSyncInterval !== savedAutoSyncInterval);
  const saveAutoSyncSettings = async () => {
    if (!Number.isFinite(draftAutoSyncInterval)) {
      setAutoSyncError('Enter a sync interval in minutes.');
      return;
    }
    if (draftAutoSyncInterval < minAutoSyncInterval || draftAutoSyncInterval > maxAutoSyncInterval) {
      setAutoSyncError(`Use ${minAutoSyncInterval} to ${maxAutoSyncInterval} minutes.`);
      return;
    }
    setAutoSyncSaving(true);
    setAutoSyncError('');
    const { ok, data } = await api('PATCH', '/api/source-sync/settings', {
      enabled: autoSyncEnabled,
      intervalMinutes: draftAutoSyncInterval,
    });
    setAutoSyncSaving(false);
    if (ok) {
      onAutoSyncSettingsChange?.(data);
      setAutoSyncEnabled(data.enabled !== false);
      setAutoSyncInterval(String(data.intervalMinutes || draftAutoSyncInterval));
    } else {
      setAutoSyncError(data?.error || 'Could not save auto-sync settings.');
    }
  };

  const handleStripeForceResync = async () => {
    setStripeResyncing(true);
    const { ok, data } = await api('POST', '/api/stripe/sync', { mode: 'checkout_sessions', force: true });
    setStripeResyncing(false);
    if (ok) {
      setStripeLastSyncedAt(new Date().toISOString());
      setBanner('connected');
      setBannerSrc('Stripe');
      setBannerMsg(`Stripe synced ${data.inserted} new orders (${data.total} total).`);
      onOrdersChanged();
    } else {
      setBanner('error'); setBannerSrc('Stripe'); setBannerMsg(data?.error || 'Sync failed');
    }
  };

  const handleStripeDisconnect = async () => {
    await api('DELETE', '/api/stripe/disconnect');
    setStripeConnected(false); setStripeAccountId(''); setStripeLastSyncedAt(null);
  };
  const handlePayPalDisconnect = async () => {
    await api('DELETE', '/api/paypal/disconnect');
    setPaypalConnected(false); setPaypalEmail(''); setPaypalLastSyncedAt(null);
  };
  const handleShopifyConnect = () => {
    setConnectModal('shopify');
  };
  const handleShopifySync = async () => {
    setShopifySyncing(true);
    const { ok, data } = await api('POST', '/api/shopify/sync', {});
    setShopifySyncing(false);
    if (ok) {
      setShopifyLastSyncedAt(new Date().toISOString());
      setBanner('connected'); setBannerSrc('Shopify'); setBannerMsg(`Shopify synced ${data.inserted} new orders (${data.total} total).`);
      onOrdersChanged();
    } else {
      setBanner('error'); setBannerSrc('Shopify'); setBannerMsg(data?.error || 'Sync failed');
    }
  };
  const handleShopifyDisconnect = async () => {
    await api('DELETE', '/api/shopify/disconnect');
    setShopifyConnected(false); setShopifyShop(''); setShopifyLastSyncedAt(null);
  };
  const handleWooConnect = () => {
    setConnectModal('woocommerce');
  };
  const handleWooSync = async () => {
    setWooSyncing(true);
    const { ok, data } = await api('POST', '/api/woocommerce/sync', {});
    setWooSyncing(false);
    if (ok) {
      setWooLastSyncedAt(new Date().toISOString());
      setBanner('connected'); setBannerSrc('WooCommerce'); setBannerMsg(`WooCommerce synced ${data.inserted} new orders (${data.total} total).`);
      onOrdersChanged();
    } else {
      setBanner('error'); setBannerSrc('WooCommerce'); setBannerMsg(data?.error || 'Sync failed');
    }
  };
  const handleWooDisconnect = async () => {
    await api('DELETE', '/api/woocommerce/disconnect');
    setWooConnected(false); setWooStoreUrl(''); setWooLastSyncedAt(null);
  };
  const handleAdobeConnect = async () => {
    setBanner(null);
    if (adobeConnection?.id && adobeConnection?.state !== 'FAILED' && adobeConnection?.setupUrl) {
      window.location.href = adobeConnection.setupUrl;
      return;
    }
    const { ok, data } = await api('POST', '/api/connectors/adobe-commerce/connect', {});
    if (ok && data.setupUrl) {
      window.location.href = data.setupUrl;
    } else {
      setBanner('error'); setBannerSrc('Adobe Commerce'); setBannerMsg(data?.error || 'Could not start setup');
    }
  };
  const handleAdobeSync = async () => {
    if (!adobeConnection?.id) return;
    setAdobeSyncing(true);
    const { ok, data } = await api('POST', `/api/connectors/adobe-commerce/${adobeConnection.id}/sync`, {});
    setAdobeSyncing(false);
    if (ok) {
      setBanner('connected'); setBannerSrc('Adobe Commerce'); setBannerMsg(`Adobe Commerce synced ${data.inserted} new orders (${data.total} total).`);
      setAdobeConnection(prev => prev ? { ...prev, lastSyncedAt:new Date().toISOString(), currentCursor:data.nextCursor || prev.currentCursor, orderCount:(prev.orderCount || 0) + (data.inserted || 0), lastError:'' } : prev);
      onOrdersChanged();
    } else {
      setBanner('error'); setBannerSrc('Adobe Commerce'); setBannerMsg(data?.error || 'Sync failed');
    }
  };
  const handleAdobeReconnect = async () => {
    if (!adobeConnection?.id) return handleAdobeConnect();
    const { ok, data } = await api('POST', `/api/connectors/adobe-commerce/${adobeConnection.id}/reconnect`, {});
    if (ok && data.setupUrl) window.location.href = data.setupUrl;
    else { setBanner('error'); setBannerSrc('Adobe Commerce'); setBannerMsg(data?.error || 'Could not start reconnect'); }
  };
  const handleAdobeDisconnect = async () => {
    if (!adobeConnection?.id) return;
    await api('DELETE', `/api/connectors/adobe-commerce/${adobeConnection.id}`);
    setAdobeConnection(prev => prev ? { ...prev, connected:false, state:'FAILED', lastError:'Disconnected' } : null);
  };
  const handleAssistedSourceSupport = source => {
    setAssistedSource(null);
    onNavigate?.('support');
  };
  const handleAvailableAction = source => {
    if (source.type === 'stripe') window.location.href = '/api/stripe/connect';
    else if (source.type === 'paypal') window.location.href = '/api/paypal/connect';
    else if (source.type === 'shopify') handleShopifyConnect();
    else if (source.type === 'woocommerce') handleWooConnect();
    else if (source.type === 'adobe_commerce') sourceSetupNote(source) && adobeConnection?.state === 'FAILED' ? handleAdobeReconnect() : handleAdobeConnect();
    else if (source.type === 'assisted') setAssistedSource(source);
    else if (source.id === 'manual_csv') setSmartCsvModal(true);
    else if (source.id === 'kickstarter') setKsModal(true);
    else if (source.id === 'indiegogo') setIgModal(true);
    else if (source.id === 'gamefound') setGfModal(true);
  };
  const syncSource = source => {
    if (source.type === 'stripe') return handleStripeForceResync();
    if (source.type === 'paypal') return setPaypalModal(true);
    if (source.type === 'shopify') return handleShopifySync();
    if (source.type === 'woocommerce') return handleWooSync();
    if (source.type === 'adobe_commerce') return handleAdobeSync();
    return null;
  };
  const disconnectSource = source => {
    if (source.type === 'stripe') return handleStripeDisconnect();
    if (source.type === 'paypal') return handlePayPalDisconnect();
    if (source.type === 'shopify') return handleShopifyDisconnect();
    if (source.type === 'woocommerce') return handleWooDisconnect();
    if (source.type === 'adobe_commerce') return handleAdobeDisconnect();
    return null;
  };
  const sourceSyncing = source => (
    source.type === 'stripe' ? stripeResyncing :
    source.type === 'shopify' ? shopifySyncing :
    source.type === 'woocommerce' ? wooSyncing :
    source.type === 'adobe_commerce' ? adobeSyncing :
    false
  );
  const availableActionLabel = source => {
    if (source.type === 'csv') return 'Upload CSV';
    if (source.type === 'adobe_commerce' && adobeConnection?.id && adobeConnection?.state !== 'FAILED') return 'Continue setup';
    if (source.type === 'adobe_commerce' && adobeConnection?.state === 'FAILED') return 'Start over';
    return 'Connect';
  };

  const SourceLogo = ({ source, size = 34 }) => (
    <div style={{ width:size, height:size, border:'1px solid #E5E7EB', borderRadius:8, background:'#fff', display:'flex', alignItems:'center', justifyContent:'center', padding:source.logo ? 5 : 0, flexShrink:0 }}>
      {source.Icon
        ? <source.Icon size={Math.round(size * 0.62)} color="#111827"/>
        : source.logo
        ? <img src={source.logo} alt={source.name} style={{ width:'100%', height:'100%', objectFit:'contain' }}/>
        : <span style={{ fontWeight:900, fontSize:12, color:'#111827' }}>{source.char}</span>
      }
    </div>
  );

  const ConnectedSourceRow = ({ source }) => {
    const syncing = sourceSyncing(source);
    const imported = sourceOrderCount(source);
    return (
      <div style={{ display:'flex', alignItems:'center', gap:14, padding:'13px 14px', borderTop:'1px solid #F3F4F6', flexWrap:'wrap' }}>
        <div style={{ flex:'1 1 260px', minWidth:0, display:'flex', alignItems:'center', gap:11 }}>
          <SourceLogo source={source}/>
          <div style={{ minWidth:0 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
              <div style={{ fontSize:14, fontWeight:900, color:'#111827' }}>{source.name}</div>
              <span style={{ ...tagStyle, color:'#065F46', borderColor:'#BBF7D0', background:'#F0FDF4' }}>Connected</span>
            </div>
            <div title={sourceConnectionDetail(source)} style={{ marginTop:3, fontSize:12, color:'#6B7280', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
              {sourceConnectionDetail(source)}
            </div>
          </div>
        </div>
        <div style={{ flex:'0 1 132px', minWidth:118 }}>
          <div style={{ fontSize:11, color:'#9CA3AF', fontWeight:800 }}>Last sync</div>
          <div style={{ fontSize:12, color:'#374151', fontWeight:800, marginTop:3 }}>{formatLastSync(sourceLastSyncedAt(source))}</div>
        </div>
        <div style={{ flex:'0 1 148px', minWidth:130 }}>
          <div style={{ fontSize:11, color:'#9CA3AF', fontWeight:800 }}>Orders imported</div>
          <div style={{ fontSize:12, color:'#374151', fontWeight:800, marginTop:3 }}>
            {ordersLoading ? <SkeletonPill width={82} height={13}/> : imported}
          </div>
        </div>
        <div style={{ flex:'0 1 290px', display:'flex', alignItems:'center', justifyContent:'flex-end', gap:8, flexWrap:'wrap', marginLeft:'auto' }}>
          <button onClick={() => syncSource(source)} disabled={syncing} style={secondaryActionBtn(syncing)}>
            <RefreshCw size={13} strokeWidth={2.2} style={{ animation:syncing ? 'spin 0.8s linear infinite' : 'none', flexShrink:0 }}/>
            {syncing ? 'Syncing...' : 'Sync orders'}
          </button>
          {source.type === 'adobe_commerce' && (
            <button onClick={handleAdobeReconnect} style={secondaryActionBtn(false)}>
              <RefreshCw size={13} strokeWidth={2.2} style={{ flexShrink:0 }}/>
              Reconnect
            </button>
          )}
          <button onClick={() => disconnectSource(source)} style={dangerActionBtn}>
            <XCircle size={13} strokeWidth={2.2} style={{ flexShrink:0 }}/>
            Disconnect
          </button>
        </div>
      </div>
    );
  };

  const AvailableSourceCard = ({ source }) => {
    const imported = sourceOrderCount(source);
    const note = sourceSetupNote(source);
    const actionLabel = availableActionLabel(source);
    const actionIcon = source.type === 'csv' ? Upload : Plus;
    const ActionIcon = actionIcon;
    return (
      <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:8, padding:14, minHeight:220, display:'flex', flexDirection:'column', gap:12 }}>
        <div style={{ display:'flex', alignItems:'flex-start', gap:11, minWidth:0 }}>
          <SourceLogo source={source} size={36}/>
          <div style={{ minWidth:0, flex:1 }}>
            <div style={{ fontSize:14, fontWeight:900, color:'#111827', lineHeight:1.2 }}>{source.name}</div>
          </div>
        </div>
        <div style={{ flex:1, fontSize:12.5, color:'#4B5563', lineHeight:1.5 }}>{source.description}</div>
        {note && (
          <div style={{ border:'1px solid #E5E7EB', background:'#F9FAFB', borderRadius:7, padding:'8px 9px', fontSize:12, color:adobeConnection?.state === 'FAILED' ? '#B42318' : '#4B5563', lineHeight:1.4 }}>
            {note}
          </div>
        )}
        {imported > 0 && (
          <div style={{ fontSize:12, color:'#6B7280', fontWeight:700 }}>
            {ordersLoading ? <SkeletonPill width={96} height={13}/> : formatOrderCount(imported)}
          </div>
        )}
        <button onClick={() => handleAvailableAction(source)} style={primaryActionBtn(false)}>
          <ActionIcon size={13} strokeWidth={2.3}/>
          {actionLabel}
        </button>
      </div>
    );
  };

  return (
    <div className="dash-page-scroll workspace-page">
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:16, marginBottom:16, flexWrap:'wrap' }}>
        <div style={{ minWidth:240, flex:'1 1 420px' }}>
          <h1 className="dash-page-title">Data sources</h1>
          <div className="dash-page-subtitle">Connect channels that feed orders into Omnifill, then normalize them into fulfillment-ready records.</div>
        </div>
        <div style={{ minWidth:220, flex:'0 1 340px', position:'relative' }}>
          <Search size={15} strokeWidth={2} style={{ position:'absolute', left:11, top:'50%', transform:'translateY(-50%)', color:'#9CA3AF', pointerEvents:'none' }}/>
          <input
            value={searchQuery}
            onChange={event => setSearchQuery(event.target.value)}
            placeholder="Search sources"
            aria-label="Search sources"
            style={{ width:'100%', border:'1px solid #D1D5DB', borderRadius:8, padding:'10px 12px 10px 34px', fontSize:13, color:'#111827', outline:'none', background:'#fff', boxShadow:'0 1px 2px rgba(15,23,42,0.03)' }}
          />
        </div>
      </div>

      {banner === 'connected' && (
        <div style={{ marginBottom:14, padding:'10px 14px', background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:8, fontSize:13, color:'#065F46', display:'flex', justifyContent:'space-between', alignItems:'center', gap:12 }}>
          <span>{bannerMsg || `${bannerSrc} account connected successfully.`}</span>
          <button onClick={() => setBanner(null)} title="Dismiss" style={{ background:'none', border:'none', color:'#6B7280', cursor:'pointer', display:'flex', padding:2 }}>
            <X size={16} strokeWidth={2.2}/>
          </button>
        </div>
      )}
      {banner === 'error' && (
        <div style={{ marginBottom:14, padding:'10px 14px', background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:8, fontSize:13, color:'#991B1B', display:'flex', justifyContent:'space-between', alignItems:'center', gap:12 }}>
          <span>{bannerSrc} connection failed: {bannerMsg}</span>
          <button onClick={() => setBanner(null)} title="Dismiss" style={{ background:'none', border:'none', color:'#6B7280', cursor:'pointer', display:'flex', padding:2 }}>
            <X size={16} strokeWidth={2.2}/>
          </button>
        </div>
      )}

      <section className="dash-panel workspace-panel-flat sources-panel" style={{ marginBottom:14 }}>
        <div className="dash-panel-header" style={{ padding:'13px 14px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
          <div>
            <h2 style={{ margin:0, fontSize:15, fontWeight:900, color:'#111827' }}>Connected</h2>
            <p style={{ margin:'3px 0 0', fontSize:12, color:'#6B7280' }}>Live sources currently authorized to sync orders.</p>
          </div>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-end', gap:10, flexWrap:'wrap' }}>
            <div style={{ fontSize:12, color:'#6B7280', fontWeight:800 }}>
              {connectionLoading ? 'Checking...' : `${connectedCount} connected`}
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', justifyContent:'flex-end' }}>
              <label style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:12, color:'#374151', fontWeight:800, cursor:autoSyncSaving ? 'wait' : 'pointer' }}>
                <input
                  type="checkbox"
                  checked={autoSyncEnabled}
                  disabled={autoSyncSaving}
                  onChange={event => { setAutoSyncEnabled(event.target.checked); setAutoSyncError(''); }}
                  style={{ width:14, height:14, flexShrink:0 }}
                />
                Auto-sync
              </label>
              <input
                type="number"
                min={minAutoSyncInterval}
                max={maxAutoSyncInterval}
                step="1"
                value={autoSyncInterval}
                disabled={autoSyncSaving}
                onChange={event => { setAutoSyncInterval(event.target.value); setAutoSyncError(''); }}
                aria-label="Auto-sync interval in minutes"
                style={{ width:74, border:'1px solid #D1D5DB', borderRadius:7, padding:'7px 8px', fontSize:12, color:'#111827', outline:'none', background:'#fff' }}
              />
              <span style={{ fontSize:12, color:'#6B7280', fontWeight:800 }}>min</span>
              <button
                onClick={saveAutoSyncSettings}
                disabled={!autoSyncDirty || autoSyncSaving}
                style={{ background:autoSyncDirty ? '#006FDD' : '#F9FAFB', color:autoSyncDirty ? '#fff' : '#9CA3AF', border:'1px solid ' + (autoSyncDirty ? '#006FDD' : '#E5E7EB'), borderRadius:7, padding:'7px 10px', fontSize:12, fontWeight:900, cursor:autoSyncDirty && !autoSyncSaving ? 'pointer' : 'not-allowed' }}
              >
                {autoSyncSaving ? 'Saving...' : 'Save'}
              </button>
              {autoSyncError && <div style={{ flexBasis:'100%', textAlign:'right', fontSize:11, color:'#DC2626', fontWeight:800 }}>{autoSyncError}</div>}
            </div>
          </div>
        </div>
        {connectedSources.length > 0
          ? connectedSources.map(source => <ConnectedSourceRow key={source.id} source={source}/>)
          : connectionLoading
            ? (
              <div style={{ padding:14, borderTop:'1px solid #F3F4F6', display:'grid', gap:10 }}>
                <SkeletonPill width="100%" height={46} style={{ borderRadius:8 }}/>
                <SkeletonPill width="72%" height={46} style={{ borderRadius:8 }}/>
              </div>
            )
            : (
              <div style={{ padding:'18px 14px', borderTop:'1px solid #F3F4F6', fontSize:13, color:'#6B7280' }}>
                {searchQuery ? 'No connected sources match your search.' : 'No live sources connected yet. Connect a source below to start syncing orders.'}
              </div>
            )
        }
      </section>

      <section className="dash-panel workspace-panel-flat sources-panel" style={{ marginBottom:14 }}>
        <div className="dash-panel-header" style={{ padding:'13px 14px' }}>
          <h2 style={{ margin:0, fontSize:15, fontWeight:900, color:'#111827' }}>Available</h2>
          <p style={{ margin:'3px 0 0', fontSize:12, color:'#6B7280' }}>Add a source, upload a CSV, or request an assisted setup.</p>
        </div>
        <div className="sources-panel-body" style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(245px, 1fr))', gap:10, padding:'0 14px 14px' }}>
          {availableSources.map(source => <AvailableSourceCard key={source.id} source={source}/>)}
          <div style={{ background:'#fff', border:'1.5px dashed #C7CDD7', borderRadius:8, padding:14, minHeight:220, display:'flex', flexDirection:'column', justifyContent:'space-between', gap:12 }}>
            <div>
              <div style={{ width:36, height:36, border:'1px solid #E5E7EB', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', background:'#F9FAFB', marginBottom:12 }}>
                <Plus size={18} strokeWidth={2.4} color="#374151"/>
              </div>
              <div style={{ fontSize:14, fontWeight:900, color:'#111827' }}>Request a source</div>
              <div style={{ fontSize:12.5, color:'#4B5563', lineHeight:1.5, marginTop:8 }}>
                Need another sales channel, marketplace, warehouse file, or custom export? Send the source name and a sample format.
              </div>
            </div>
            <button onClick={() => onNavigate?.('support')} style={primaryActionBtn(false)}>
              <Plus size={13} strokeWidth={2.3}/>
              Request a source
            </button>
          </div>
        </div>
      </section>

      <div style={{ padding:'11px 14px', background:'#F9FAFB', border:'1px solid #E5E7EB', borderRadius:8, fontSize:12.5, color:'#6B7280', lineHeight:1.45 }}>
        Connection details and imported order data are used only to sync orders and prepare exports. See the{' '}
        <a href="/privacy-policy" target="_blank" rel="noreferrer" style={{ color:'#006FDD', fontWeight:800, textDecoration:'none' }}>privacy policy</a>.
      </div>

      {paypalModal && (
        <PayPalSyncModal
          onClose={() => setPaypalModal(false)}
          onImported={(data) => { setPaypalLastSyncedAt(data?.lastSyncedAt || new Date().toISOString()); onOrdersChanged(); }}
        />
      )}
      {connectModal && (
        <StoreConnectModal
          source={connectModal}
          onClose={() => setConnectModal(null)}
        />
      )}
      {assistedSource && (
        <AssistedSourceConnectModal
          source={assistedSource}
          onClose={() => setAssistedSource(null)}
          onOpenSupport={handleAssistedSourceSupport}
        />
      )}
      {smartCsvModal && (
        <SmartCsvImportModal
          onClose={() => setSmartCsvModal(false)}
          onImported={() => { setSmartCsvModal(false); onOrdersChanged(); }}
        />
      )}
      {ksModal && (
        <KickstarterCsvModal
          onClose={() => setKsModal(false)}
          onImported={() => { setKsModal(false); onOrdersChanged(); }}
        />
      )}
      {igModal && (
        <IndiegogoCsvModal
          onClose={() => setIgModal(false)}
          onImported={() => { setIgModal(false); onOrdersChanged(); }}
        />
      )}
      {gfModal && (
        <GamefoundCsvModal
          onClose={() => setGfModal(false)}
          onImported={() => { setGfModal(false); onOrdersChanged(); }}
        />
      )}
    </div>
  );
};
